/**
 * Project tools (RFC 0263/0265): the agent-facing tools over a project's
 * collaborative state — its sessions and its documents.
 *
 * They live in mod-core, not a client, because every host that serves inference
 * shares them: the cli's headless `workers` command today, web self-serve
 * ("session" mode) and a hosted deployment next, iOS via FFI later. One shared
 * `create_document`, not a copy per client (RFC 0263). Every `execute` is a
 * `repo.find` plus a model projection or mutation — no filesystem, no process —
 * which is exactly what lets a host with no machine behind it run them.
 *
 * Execution/claiming stays a host concern (the ToolExecutor seam); these are
 * only the definitions.
 */

import {
  type AnyDocumentId,
  isValidDocumentId,
  type Repo,
} from '@automerge/automerge-repo/slim'
import { BranchLog } from '../log/branch-log'
import type { RunDispatch, StopEvent } from '../log/types'
import { type IconSpec, iconSpecError } from '../model/apps/app-icon'
import { updateApp } from '../model/apps/mutations'
import { installedApps, readApp, readSlotFills } from '../model/apps/reads'
import type { AppCapabilities, AppSchemaSpec } from '../model/apps/types'
import {
  createComment,
  deleteComment,
  resolveComment,
} from '../model/comments/mutations'
import { projectThreads } from '../model/comments/reads'
import { loadComments } from '../model/comments/store'
import type {
  CommentEntry,
  Mention,
  MentionKind,
} from '../model/comments/types'
import { mimeFromPath } from '../model/documents/cross'
import { projectChanges } from '../model/documents/history'
import { moveToProject } from '../model/documents/move-project'
import {
  createDocument,
  deleteDocument,
  moveDocument,
  moveFolder,
  normalizeDocumentPath,
  renameDocument,
  writeDocumentContent,
  writeDocumentData,
} from '../model/documents/mutations'
import {
  projectDocuments,
  readDocumentBytes,
  readDocumentContent,
  readDocumentData,
} from '../model/documents/reads'
import { restoreVersion } from '../model/documents/restore'
import { projectAuthor } from '../model/people/reads'
import { projectString } from '../model/project/scalars'
import type { ProjectDoc } from '../model/project/types'
import { changeNote } from '../model/session/agent-edits'
import {
  addSessionProject,
  createSession,
  nameSessionOnce,
  removeSessionProject,
  setSessionCompleted,
} from '../model/session/mutations'
import { mayNotEditReason } from '../model/session/pairings'
import {
  isSessionDone,
  projectSessionPendingInteractions,
  projectSessionStatus,
  projectSessionTranscript,
  registryStatus,
} from '../model/session/reads'
import { recordTouch } from '../model/session/record-touch'
import { sessionTitleOf, titleSetBy } from '../model/session/title'
import { SessionTaskLog } from '../model/tasks/mutations'
import {
  branchDocuments,
  copyOnBranch,
  createBranch,
  createOnBranch,
  deleteOnBranch,
  findBranch,
  readOnBranch,
  writeOnBranch,
} from '../model/versions/branches'
import {
  listVersions,
  readAtVersion,
  saveVersion,
  type Version,
  versionsOf,
} from '../model/versions/versions'
import { makeProject } from '../model/world/convert-folder'
import { moveFolderWithProjects } from '../model/world/mounts'
import { projectWorldProjects } from '../model/world/reads'
// Where model/ reaches into services/, and why. `send_message` IS a queued run,
// and enqueueRun is the only path that both appends the message and queues one
// (and that knows a captured session must not get a run). `answerComment` is the
// same argument for RFC 0289: answering a comment wakes the session that wrote
// it, and a second copy of that rule here is how the tool and the bridge drift
// into disagreeing about who gets told. Reimplementing either is how they drift.
// Safe from cycles: services/ reaches model/ only by deep path (mutations,
// reads), and neither imports this file nor the model barrel.
import { answerComment } from '../services/comments/answer-comment'
import {
  type DeliverMentionsResult,
  deliverMentions,
} from '../services/comments/deliver-mentions'
import {
  type RegistryQuery,
  registryHolding,
} from '../services/comments/registry-holding'
import {
  answerInteraction,
  dismissInteraction,
} from '../services/run/answer-interaction'
import { startBranchSession } from '../services/run/branch-session'
import { enqueueRun } from '../services/run/enqueue-run'
import { cleanTitle } from '../workers/session-namer'
import type { InteractionAnswerValue } from './ask-user-question'
import { parseQuestionBody } from './ask-user-question'
import { attachmentDocId, forwardAttachments } from './forward-attachments'
import { modSkillText } from './mod-skills'
/**
 * What a tool may answer with when words are not enough (RFC 0303 3d).
 *
 * Almost every tool returns a string, and should: text is the only thing every
 * host, driver and provider can carry. An image is the exception a browser
 * forces, because a chart, a map or a layout is a fact about a page that no
 * accessibility tree states. So the shape is text PLUS an optional image, never
 * an image alone: a host that cannot show the picture still gets the sentence.
 */
export interface ToolOutput {
  text: string
  image?: {
    mediaType: 'image/png' | 'image/jpeg'
    /** Base64, because every carrier of this (MCP, the proxy) takes base64. */
    base64: string
  }
}

/** ai-sdk-ish tool shape: description + a JSON-schema for inputs + an executor.
 *  Shared by every toolset (filesystem tools in the cli use it too). */
export interface ToolDef {
  description: string
  inputSchema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: string[]
  }
  execute: (
    args: Record<string, unknown>,
  ) => Promise<string | ToolOutput> | string | ToolOutput
}

/**
 * The words out of a tool result, whatever shape it came in.
 *
 * Every caller that can only carry text uses this, so a tool that grows an
 * image does not quietly answer `[object Object]` on the hosts that cannot
 * show one.
 */
export function toolResultText(value: string | ToolOutput): string {
  return typeof value === 'string' ? value : value.text
}

/** The image a tool result carries, if any. */
export function toolResultImage(
  value: string | ToolOutput,
): ToolOutput['image'] | undefined {
  return typeof value === 'string' ? undefined : value.image
}

/**
 * Read a textual tool argument after a trip through the Automerge log.
 *
 * Tool-call string leaves come back as `ImmutableString` wrappers rather than
 * native JS strings. Tests and direct callers usually hand us native strings,
 * so a `typeof value === 'string'` guard works there while silently dropping
 * the same argument in the app.
 */
export function toolArgString(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (value !== null && typeof value === 'object') {
    const inner = (value as { val?: unknown }).val
    if (typeof inner === 'string') return inner
  }
  return undefined
}

async function ready(handle: {
  whenReady?: () => Promise<void>
}): Promise<void> {
  await handle.whenReady?.()
}

/**
 * Render a projected transcript as plain text (role: text), for a tool result.
 *
 * TAILED by default (RFC 0286). A long design conversation reached 129,676
 * characters, which is not a tool result: it overflows the caller and has to be
 * spooled to a file and grepped, so the cheap "what is this session up to"
 * check costs more context than the work being checked on. Anyone sweeping
 * several sessions would spend their whole budget on the first one.
 *
 * The tail is the right default because recency is what the question is about.
 * `messages: 0` reads the whole thing for the times it genuinely is a read.
 */
function transcriptToText(doc: unknown, messages: number): string {
  const transcript = projectSessionTranscript(doc)
  const tailed =
    messages > 0 && transcript.length > messages
      ? transcript.slice(-messages)
      : transcript
  const omitted = transcript.length - tailed.length
  const body =
    transcript.length === 0
      ? '(no messages)'
      : tailed
          .map((m) => {
            const text = m.content
              .map((b) => (b.type === 'text' ? b.text : `[${b.type}]`))
              .join('')
            return `${m.role}: ${text}`
          })
          .join('\n\n')
  // Say what was cut, and where the rest is. A truncated transcript that does
  // not announce itself reads as a session that started mid-sentence.
  const head =
    omitted > 0
      ? `(showing the last ${tailed.length} of ${transcript.length} messages; ` +
        'pass messages: 0 for all)\n\n'
      : ''
  const pending = pendingAskToText(doc)
  return pending ? `${head}${body}\n\n${pending}` : `${head}${body}`
}

/**
 * The question this session is stopped on, if any (RFC 0286).
 *
 * Appended to `read_session` rather than given a tool of its own. A pending
 * question IS the session's current state, so a reader who does not get it here
 * would read a transcript that ends mid-thought and conclude the agent had
 * simply stopped talking. It also carries the `interactionId`, which is what
 * `answer_question` needs and nothing else surfaces.
 */
function pendingAskToText(doc: unknown): string {
  const asks = projectSessionPendingInteractions(doc)
  if (asks.length === 0) return ''
  return asks
    .map((ask) => {
      const lines = [
        `[WAITING FOR AN ANSWER] ${ask.title}`,
        `  interactionId: ${ask.interactionId}`,
      ]
      let questions: ReturnType<typeof parseQuestionBody>['questions'] = []
      try {
        questions = parseQuestionBody(ask.bodyJson).questions
      } catch {
        // A body this peer cannot parse is still worth reporting as pending:
        // the session is stopped either way, and saying nothing would hide it.
        lines.push('  (question body could not be read on this peer)')
      }
      for (const q of questions) {
        lines.push(`  question ${q.id}: ${q.prompt}`)
        for (const o of q.options) lines.push(`    ${o.value}  ${o.label}`)
        if (q.allowFreeText) lines.push('    (free text allowed)')
      }
      return lines.join('\n')
    })
    .join('\n\n')
}

/** The mime a path's extension implies; it lives with the document model
 *  (RFC 0357 S16 reads it for a file becoming a document) and is re-exported
 *  here for every surface that creates documents through this module. */
export { mimeFromPath }

/**
 * How many sessions `list_sessions` will open to report status when asked
 * (RFC 0286). A list is read, not swept: twenty is more than anyone is steering
 * at once, and the cap is what keeps an opt-in read from hydrating a project
 * that has accumulated hundreds of sessions.
 */
const DEFAULT_STATE_LIMIT = 20

/**
 * How many messages `read_session` returns when not told otherwise (RFC 0286).
 *
 * Enough to see how a session got where it is, small enough that checking four
 * of them costs less than the work being checked on. The pending question is
 * appended regardless of this bound: a session stopped on a question must never
 * be truncated into looking quiet.
 */
const DEFAULT_TRANSCRIPT_MESSAGES = 12

/**
 * How long `send_message` waits to see a worker claim the run it queued
 * (RFC 0286). Long enough that a busy daemon is not reported as absent, short
 * enough that the caller is not held up: the answer is about whether anything
 * is LISTENING, not about whether the work is done.
 */
const CLAIM_POLL_MS = 500
const CLAIM_POLLS = 10

/**
 * Wait to see a worker claim a run that was just queued, and report what the
 * session is actually doing.
 *
 * Queuing a run always succeeds: a project whose daemon is not running accepts
 * every message and answers none of them, so reporting on the write alone would
 * call a project with no responder a successful dispatch. `queued` after the
 * wait is the honest answer, and it is a different problem from a slow agent.
 */
async function awaitClaim(repo: Repo, sessionId: string): Promise<string> {
  const session = await repo.find(sessionId as AnyDocumentId)
  await ready(session as { whenReady?: () => Promise<void> })
  for (let i = 0; i < CLAIM_POLLS; i++) {
    await new Promise((r) => setTimeout(r, CLAIM_POLL_MS))
    const status = projectSessionStatus(session.doc())
    if (status !== 'queued') return status
  }
  return 'queued'
}

/**
 * RFC 0286 §5a — the dispatch record for a tool call that asked to hear back.
 *
 * Returns null when `notify` was not asked for, which is the default and the
 * whole design: a session doing long multi-turn work would otherwise wake its
 * sender on every turn, and an orchestrator woken constantly is back to checking
 * windows. Hearing back is a thing you ask for.
 *
 * Throws when there is no session to reply TO. These tools also run in hosts
 * with no session of their own (a one-shot CLI call), and a notify accepted with
 * nowhere to deliver would be a promise silently never kept.
 */
function dispatchFor(
  a: Record<string, unknown>,
  selfSessionId?: string,
): RunDispatch | null {
  if (a.notify !== true) return null
  if (!selfSessionId) {
    throw new Error(
      'notify needs a session to reply to, and this caller has none; ' +
        'send without notify and read the session when you want the answer',
    )
  }
  // RFC 0348 3h: the events the caller wants; both unless it names fewer.
  const named = Array.isArray(a.notifyOn)
    ? a.notifyOn
        .map((v) => String(v))
        .filter((v): v is StopEvent => v === 'ended' || v === 'asked')
    : []
  return {
    fromSessionId: selfSessionId,
    dispatchId: newDispatchId(),
    ...(named.length ? { events: named } : {}),
  }
}

/** A dispatch id, unique per dispatch so a sender with several outstanding can
 *  tell the replies apart without matching on text. */
function newDispatchId(): string {
  const uuid = (
    globalThis.crypto as { randomUUID?: () => string } | undefined
  )?.randomUUID?.()
  return `d-${uuid ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`}`
}

/** Whether the World lists a project (RFC 0336 S3). */
export async function worldListsProject(
  repo: Repo,
  worldId: string | null | undefined,
  projectId: string,
): Promise<boolean> {
  if (!worldId) return false
  try {
    const world = await repo.find(worldId as AnyDocumentId)
    await ready(world as { whenReady?: () => Promise<void> })
    return projectWorldProjects(world.doc()).some(
      (p) => p.projectId === projectId,
    )
  } catch {
    return false
  }
}

/**
 * Tools over a project's SESSIONS: list them, read one, hand one work, answer
 * one that stopped to ask, file one as done (RFC 0286).
 *
 * Was `sessionReadTools`, which was an accurate name for half a surface: an
 * agent could see that other sessions existed and read what they said, but
 * nothing here could write to one, so a person was the only way work ever moved
 * between them. The writes are both appends to a session's log, which is why
 * this stayed one module rather than growing an orchestration layer beside it.
 *
 * `selfSessionId` is the session these tools were built FOR, when there is one.
 * It refuses a send to itself, and it is the address a `notify` dispatch is
 * answered to (RFC 0286 §5a) — a host with no session of its own can dispatch,
 * but cannot ask to hear back, because there is nowhere to hear it.
 */
export function sessionTools(
  repo: Repo,
  projectId: string,
  selfSessionId?: string,
  opts: { worldId?: string | null } = {},
): Record<string, ToolDef> {
  return {
    list_sessions: {
      description:
        'List the sessions (conversations) in this project, as "id  title" ' +
        'lines. Sessions filed as done are hidden unless includeCompleted is ' +
        'true. Pass withState to also report what each one is DOING (asking / ' +
        'running / queued / idle); that opens each session document, so it is ' +
        'capped by limit.',
      inputSchema: {
        type: 'object',
        properties: {
          includeCompleted: {
            type: 'boolean',
            description:
              'Include sessions filed as done. They accumulate forever and ' +
              'nobody is steering them, so they are hidden by default.',
          },
          withState: {
            type: 'boolean',
            description:
              'Report each session\'s status. "asking" means it stopped to ask ' +
              'a person and nothing moves until someone answers.',
          },
          limit: {
            type: 'number',
            description:
              'How many sessions to open when withState is set (default 20). ' +
              'Rows past the cap still list, with status unknown.',
          },
        },
      },
      execute: async (a) => {
        const handle = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        const doc = handle.doc()
        const ids = (doc?.sessionIds ?? [])
          .map(projectString)
          .filter((id): id is string => !!id)
        const meta = doc?.sessionMeta ?? {}
        if (ids.length === 0) return '(no sessions)'
        const includeCompleted = a.includeCompleted === true
        // Most-recently-updated FIRST. `sessionIds` is in creation order, so
        // slicing it for the cap below would hydrate the OLDEST sessions and
        // report them as "the most recent", which is worse than no cap: the ones
        // being steered are exactly the ones that would read as unknown.
        // Done is a read rule (isSessionDone): filed AND quiet since. A filed
        // session that started working again is open, and listed as such.
        const now = Date.now()
        const done = (id: string) =>
          isSessionDone({
            ...meta[id],
            status: registryStatus(meta[id], now),
          })
        const open = ids
          .filter((id) => includeCompleted || !done(id))
          .sort((x, y) => (meta[y]?.updatedAt ?? 0) - (meta[x]?.updatedAt ?? 0))
        // Distinguish "no sessions" from "none you have not filed": the second is
        // an achievement and the first is an empty project.
        if (open.length === 0) return '(no open sessions)'

        const label = (id: string) =>
          `${id}  ${meta[id]?.title ?? '(untitled)'}${
            done(id) ? '  (done)' : ''
          }`
        if (a.withState !== true) return open.map(label).join('\n')

        // Hydration starts here, and only here. The registry read above is what
        // the zero-hydration contract protects; this is the opt-in that pays a
        // document per row for the four statuses no projection of the registry
        // can reach.
        const limit =
          typeof a.limit === 'number' && a.limit > 0
            ? Math.floor(a.limit)
            : DEFAULT_STATE_LIMIT
        const read = open.slice(0, limit)
        const statuses = await Promise.all(
          read.map(async (id) => {
            try {
              const session = await repo.find(id as AnyDocumentId)
              await ready(session as { whenReady?: () => Promise<void> })
              return projectSessionStatus(session.doc())
            } catch {
              // An unreachable session is UNKNOWN, never 'idle'. Reporting a
              // session nobody could open as quiet is how a stuck one gets
              // skipped by whoever is triaging.
              return 'unknown'
            }
          }),
        )
        const lines = read.map((id, i) => `${statuses[i]}\t${label(id)}`)
        // The unread tail is COUNTED, not listed. Printing one `unknown` row per
        // session buried six useful lines under forty in a real project, which
        // is the same failure as showing nothing: the answer was in there and
        // nobody was going to find it. Drop withState to list every session
        // cheaply, or raise the limit to read further.
        if (open.length > limit) {
          // Say what was skipped. A bound that is not stated reads as coverage
          // it does not have.
          lines.push(
            `(${open.length - limit} older session${
              open.length - limit === 1 ? '' : 's'
            } not read; status unknown, ` +
              'not idle. Raise limit, or drop withState to list them all.)',
          )
        }
        return lines.join('\n')
      },
    },
    start_session: {
      description:
        'Open a NEW session in this project (or the one projectId names) and ' +
        'give it its first piece of ' +
        'work. Use this for work that deserves its own thread: it gets its own ' +
        'context, its own transcript, and can be watched and steered ' +
        'separately. Returns the new session id.',
      inputSchema: {
        type: 'object',
        properties: {
          title: {
            type: 'string',
            description:
              'What this session is for, shown in the list until the new ' +
              'session names itself after its first turn. Name the work, not ' +
              "the request. With taskId, the task's title is used instead.",
          },
          text: {
            type: 'string',
            description:
              'The first message. Omit to open the session without starting a ' +
              'turn, e.g. when a person will take it from here.',
          },
          agent: {
            type: 'string',
            description: 'claude-code, codex, or cursor.',
          },
          skills: {
            type: 'array',
            items: { type: 'string' },
            description: 'World skill ids the first run should be given.',
          },
          attachments: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Attachments to pass on, as the paths you were handed for them ' +
              '(…/mod-attachments/<id>/<name>) or their document ids. They ' +
              'arrive as real attachments, not as text.',
          },
          notify: {
            type: 'boolean',
            description:
              'Hear back when that first turn ends. The reply arrives on YOUR ' +
              'session as an event with a one-line summary, and starts a turn ' +
              'here when you are next free. Off by default, EXCEPT with ' +
              'context: a branch reports back unless you set this to false.',
          },
          notifyOn: {
            type: 'array',
            items: { type: 'string', enum: ['ended', 'asked'] },
            description:
              'With notify: which events to hear. "ended" when a turn ' +
              'finishes, fails or is stopped; "asked" when it waits on a ' +
              'question. Both unless you name fewer.',
          },
          taskId: {
            type: 'string',
            description:
              'A pending task this session takes: it becomes active with the ' +
              'new session as its worker, so the session keeps working toward ' +
              'it. Refused when the task is no longer pending.',
          },
          projectId: {
            type: 'string',
            description:
              'The project the new session works in, as a search result ' +
              "names it. Its tools and checkout are that project's. " +
              'Omit for this project. Not with context: a branch stays here.',
          },
          context: {
            type: 'boolean',
            description:
              'Give it the conversation it came out of, so it does not start ' +
              'blind or have to ask you what you already know. Use it for work ' +
              'that branches off what you are doing; leave it off for work ' +
              'that stands on its own. Off by default.',
          },
        },
        required: ['title'],
      },
      execute: async (a) => {
        const title = String(a.title ?? '').trim()
        if (!title) throw new Error('title is required')
        const text = String(a.text ?? '').trim()
        const dispatch = text ? dispatchFor(a, selfSessionId) : null
        const agent = toolArgString(a.agent)?.trim()
        const skills = Array.isArray(a.skills)
          ? a.skills.map(String)
          : undefined
        // RFC 0336 S3: creating is the one place a project is named. It must
        // be one the World lists, so a typo cannot mint a session nowhere.
        const asked = toolArgString(a.projectId)?.trim()
        if (asked && asked !== projectId) {
          if (a.context === true) {
            throw new Error('a branch (context) stays in this project')
          }
          if (!(await worldListsProject(repo, opts.worldId, asked))) {
            throw new Error(`${asked} is not a project in this World`)
          }
        }
        const taskId = toolArgString(a.taskId)?.trim()
        // A session started for a task works where the task lives: its tools,
        // its checkout. A project named that disagrees is refused before
        // anything is written.
        const taskHome = taskId
          ? await registryHolding(repo, asked || projectId, opts.worldId, {
              taskId,
            })
          : null
        if (taskHome && asked && asked !== taskHome) {
          throw new Error(
            `task ${taskId} is in project ${taskHome}, not ${asked}; start it there`,
          )
        }
        const target = taskHome ?? (asked || projectId)
        const tasks = taskHome
          ? new SessionTaskLog(repo as never, undefined, taskHome)
          : null
        const task = taskId ? await tasks?.get(taskId) : null
        if (taskId && task?.status !== 'pending') {
          return `Task ${taskId} is not pending; someone may have taken it. Re-read it before starting a session.`
        }

        // A BRANCH (RFC 0302 S4): a child that carries the conversation it came
        // out of and inherits this session's harness, rather than one opened
        // blind on whatever the device prefers. Needs a first message: the
        // context rides on it, and there is nothing to carry it on otherwise.
        if (a.context === true && selfSessionId && text && !taskId) {
          // A branch REPORTS BACK unless told not to (RFC 0302 S8). It exists
          // to serve the conversation it came out of, so its conclusion is owed
          // there, and a coordinator that had to remember to ask is a
          // coordinator that eventually forgets and waits forever. That is the
          // whole of what 0304 needs to coordinate focused work: no
          // space-owned agent, no polling, an event and a queued turn.
          const owed =
            a.notify === false
              ? null
              : (dispatch ?? dispatchFor({ ...a, notify: true }, selfSessionId))
          const branch = await startBranchSession(repo, {
            projectId,
            fromSessionId: selfSessionId,
            title,
            text,
            ...(agent ? { agent } : {}),
            ...(skills ? { skills } : {}),
            ...(owed ? { dispatch: owed } : {}),
          })
          const state = await awaitClaim(repo, branch.sessionId)
          const told = owed ? ' You will be told when that turn ends.' : ''
          return (
            `branched ${branch.sessionId} (${title}) from this session on ` +
            `${branch.agent ?? 'the default harness'}; it has the conversation ` +
            `so far and it is ${state}.${told}`
          )
        }

        // The parent is recorded so the list can stop showing a session nobody
        // asked for at the top level. An agent that opens six sub-sessions to
        // fan work out buries the six conversations a person actually started,
        // and the honest place to find one is the thread that opened it.
        // RFC 0336 S11: resolved before the session exists, so a bad path
        // leaves nothing behind.
        const attachments =
          text && Array.isArray(a.attachments)
            ? await forwardAttachments(repo as never, a.attachments)
            : []
        // The same rule as a session started from a page (RFC 0311 3b): a
        // task's title was written by someone on purpose and stays; otherwise
        // the opener's title is a placeholder the new session's own agent
        // renames after its first turn, once it knows what the work is.
        const taskTitle = task?.title ? String(task.title) : ''
        const handle = await createSession(repo, target, {
          title: taskTitle || title,
          titleSetBy: taskTitle ? 'you' : 'stub',
          ...(selfSessionId ? { parentSessionId: selfSessionId } : {}),
        })
        const sessionId = handle.documentId as string
        // Taken before the first run is queued, so that run already has the
        // task as its goal (RFC 0304 3c).
        if (taskId && !(await tasks?.take(taskId, sessionId))) {
          return `opened ${sessionId} (${title}), but task ${taskId} was taken by someone else first; nothing was sent.`
        }

        if (!text) {
          return `opened ${sessionId} (${title}) with no first message`
        }
        await enqueueRun(repo, sessionId, text, {
          projectId: target,
          ...(attachments.length ? { attachments } : {}),
          ...(agent ? { agent } : {}),
          ...(skills ? { skills } : {}),
          ...(dispatch ? { dispatch } : {}),
          // RFC 0348 3b: the opener's errand, reported back even on screen.
          ...(selfSessionId ? { sentBy: selfSessionId } : {}),
        })
        const status = await awaitClaim(repo, sessionId)
        const heard = dispatch ? ' You will be told when that turn ends.' : ''
        return status === 'queued'
          ? `opened ${sessionId} (${title}), but no worker has claimed its ` +
              'first run yet; the message is on the log and nothing is working ' +
              `on it right now.${heard}`
          : `opened ${sessionId} (${title}) and it is ${status}.${heard}`
      },
    },
    send_message: {
      description:
        'Send a message to ANOTHER session and start a turn there, as if a ' +
        'person had typed it. Returns whether a worker picked the run up: ' +
        '"queued" means the message landed but nothing is answering it yet.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: {
            type: 'string',
            description: 'The session to send to (from list_sessions)',
          },
          text: {
            type: 'string',
            description: 'The message. Written as a user message on that log.',
          },
          agent: {
            type: 'string',
            description:
              'Which harness answers: claude-code, codex, or cursor. Omit for ' +
              "the responding device's default.",
          },
          skills: {
            type: 'array',
            items: { type: 'string' },
            description: 'World skill ids the run should be given.',
          },
          attachments: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Attachments to pass on, as the paths you were handed for them ' +
              '(…/mod-attachments/<id>/<name>) or their document ids. They ' +
              'arrive as real attachments, not as text.',
          },
          notify: {
            type: 'boolean',
            description:
              'Hear back when that turn ends. The reply arrives on YOUR ' +
              'session as an event with a one-line summary, and starts a turn ' +
              'here when you are next free. Off by default: a session doing ' +
              'long multi-turn work would otherwise wake you every turn.',
          },
          notifyOn: {
            type: 'array',
            items: { type: 'string', enum: ['ended', 'asked'] },
            description:
              'With notify: which events to hear. "ended" when a turn ' +
              'finishes, fails or is stopped; "asked" when it waits on a ' +
              'question. Both unless you name fewer.',
          },
        },
        required: ['sessionId', 'text'],
      },
      execute: async (a) => {
        const sessionId = String(a.sessionId ?? a.session_id ?? '').trim()
        const text = String(a.text ?? '').trim()
        if (!sessionId) throw new Error('sessionId is required')
        if (!text) throw new Error('text is required')
        // Sending to yourself queues a run on the log you are mid-turn on. It is
        // never what someone means by "hand this work off", and the resulting
        // second run on your own session is confusing to unpick afterwards.
        if (selfSessionId && sessionId === selfSessionId) {
          throw new Error(
            'that is this session; send_message hands work to a DIFFERENT session',
          )
        }
        // RFC 0336 S1: the session's own project, which may not be this one.
        // The run is queued there, so its host, tools and checkout are its own.
        const home = await registryHolding(repo, projectId, opts.worldId, {
          sessionId,
        })
        const ws = await repo.find<ProjectDoc>(home as AnyDocumentId)
        await ready(ws as { whenReady?: () => Promise<void> })
        if (
          !(ws.doc()?.sessionIds ?? []).some(
            (id) => projectString(id) === sessionId,
          )
        ) {
          throw new Error(`${sessionId} is not a session in any project here`)
        }

        const dispatch = dispatchFor(a, selfSessionId)
        const agent = toolArgString(a.agent)?.trim()
        // RFC 0336 S11: the same documents, pointed at again, before anything
        // is queued, so a bad path refuses the whole send.
        const attachments = Array.isArray(a.attachments)
          ? await forwardAttachments(repo as never, a.attachments)
          : []
        await enqueueRun(repo, sessionId, text, {
          projectId: home,
          ...(attachments.length ? { attachments } : {}),
          ...(agent ? { agent } : {}),
          ...(Array.isArray(a.skills) ? { skills: a.skills.map(String) } : {}),
          ...(dispatch ? { dispatch } : {}),
          ...(selfSessionId ? { sentBy: selfSessionId } : {}),
        })

        // Then WATCH, rather than reporting success on the write alone.
        const status = await awaitClaim(repo, sessionId)
        const heard = dispatch
          ? ' You will be told when that turn ends; do not poll for it.'
          : ''
        return status === 'queued'
          ? `sent to ${sessionId}, but no worker has claimed it after ` +
              `${(CLAIM_POLLS * CLAIM_POLL_MS) / 1000}s. The message is on the ` +
              'log and will be answered whenever a device that serves this ' +
              `project comes back; nothing is working on it right now.${heard}`
          : `sent to ${sessionId} (${status})${heard}`
      },
    },
    answer_question: {
      description:
        'Answer a question another session stopped to ask, so its turn ' +
        'resumes. Get the interactionId and the question ids from ' +
        "read_session. Answer on the person's behalf only when you already " +
        'know what they want; otherwise tell them it is waiting.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: {
            type: 'string',
            description: 'The session that is waiting',
          },
          interactionId: {
            type: 'string',
            description:
              'From read_session, on the [WAITING FOR AN ANSWER] line',
          },
          answers: {
            type: 'object',
            description:
              'Keyed by question id: { "q1": { "selected": ["value"] } }. Use ' +
              'freeText for a written answer. Every question in the ask needs ' +
              'an entry.',
          },
          dismiss: {
            type: 'boolean',
            description:
              'Decline instead of answering. The agent is told to proceed on ' +
              'its own judgment, which beats leaving it stopped.',
          },
        },
        required: ['sessionId', 'interactionId'],
      },
      execute: async (a) => {
        const sessionId = String(a.sessionId ?? a.session_id ?? '').trim()
        const interactionId = String(a.interactionId ?? '').trim()
        if (!sessionId) throw new Error('sessionId is required')
        if (!interactionId) throw new Error('interactionId is required')

        // RFC 0336 S1: answered in the session's own project, so its status,
        // its row and a task-bound question's task move there, not here.
        const home = await registryHolding(repo, projectId, opts.worldId, {
          sessionId,
        })
        const tasks = new SessionTaskLog(repo as never, undefined, home)
        const answerOptions = { projectId: home, tasks }
        if (a.dismiss === true) {
          const ok = await dismissInteraction(
            repo,
            sessionId,
            interactionId,
            answerOptions,
          )
          return ok
            ? `dismissed ${interactionId}; the session was told to use its own judgment`
            : 'that question was already answered or cancelled'
        }

        const answers = a.answers
        if (!answers || typeof answers !== 'object') {
          throw new Error('answers is required (or pass dismiss: true)')
        }
        // Normalise before writing: a model reasonably passes a bare string or a
        // list, and rejecting that would leave a session stopped over a shape.
        const normalised: Record<string, InteractionAnswerValue> = {}
        for (const [key, raw] of Object.entries(
          answers as Record<string, unknown>,
        )) {
          if (typeof raw === 'string') normalised[key] = { selected: [raw] }
          else if (Array.isArray(raw))
            normalised[key] = { selected: raw.map(String) }
          else if (raw && typeof raw === 'object') {
            const v = raw as { selected?: unknown; freeText?: unknown }
            normalised[key] = {
              selected: Array.isArray(v.selected) ? v.selected.map(String) : [],
              ...(typeof v.freeText === 'string'
                ? { freeText: v.freeText }
                : {}),
            }
          }
        }

        const ok = await answerInteraction(
          repo,
          sessionId,
          interactionId,
          normalised,
          answerOptions,
        )
        // Answering twice is a normal race, not an error: two surfaces can show
        // the same question and the first answer wins.
        return ok
          ? `answered ${interactionId}; the session resumes`
          : 'that question was already answered or cancelled'
      },
    },
    complete_session: {
      description:
        'File a session as done, or un-file it, so it leaves (or rejoins) the ' +
        'open list. This is a record of a DECISION, not of an event: an agent ' +
        'going idle does not mean its work is done, so only file a session when ' +
        'the person asked for the work it finished.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: {
            type: 'string',
            description: 'The session id to file (from list_sessions)',
          },
          done: {
            type: 'boolean',
            description: 'True to file as done (default), false to un-file.',
          },
        },
        required: ['sessionId'],
      },
      execute: async (a) => {
        const sessionId = String(a.sessionId ?? a.session_id ?? '').trim()
        if (!sessionId) throw new Error('sessionId is required')
        // RFC 0336 S1: filed in the project that lists the session.
        const home = await registryHolding(repo, projectId, opts.worldId, {
          sessionId,
        })
        const handle = await repo.find<ProjectDoc>(home as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        // Guard the id mix-up the same way read_session does: filing an id that
        // no project lists would otherwise write a registry entry for a
        // session that does not exist and report success.
        if (
          !(handle.doc()?.sessionIds ?? []).some(
            (id) => projectString(id) === sessionId,
          )
        ) {
          throw new Error(`${sessionId} is not a session in this project`)
        }
        const done = a.done !== false
        await setSessionCompleted(repo, home, sessionId, done)
        return done ? `filed ${sessionId} as done` : `un-filed ${sessionId}`
      },
    },
    read_session: {
      description:
        "Read the end of a session's transcript, given its id (from " +
        'list_sessions), plus any question it has stopped to ask. Defaults to ' +
        `the last ${DEFAULT_TRANSCRIPT_MESSAGES} messages, which is what "what ` +
        'is this session up to" needs; pass messages: 0 for the whole thing.',
      inputSchema: {
        type: 'object',
        properties: {
          sessionId: {
            type: 'string',
            description: 'The session id to read',
          },
          messages: {
            type: 'number',
            description:
              'How many of the most recent messages to return. 0 means all, ' +
              'which on a long session can be over a hundred thousand ' +
              'characters, so ask for it deliberately.',
          },
        },
        required: ['sessionId'],
      },
      execute: async (a) => {
        const sessionId = String(a.sessionId ?? a.session_id ?? '').trim()
        if (!sessionId) throw new Error('sessionId is required')
        const handle = await repo.find(sessionId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        // A project doc has sessionIds; a session doc does not — guard against
        // the common id mix-up so the agent gets a clear error, not an empty read.
        const doc = handle.doc() as Record<string, unknown> | undefined
        if (doc && 'sessionIds' in doc) {
          throw new Error(`${sessionId} is a project, not a session`)
        }
        const messages =
          typeof a.messages === 'number' && a.messages >= 0
            ? Math.floor(a.messages)
            : DEFAULT_TRANSCRIPT_MESSAGES
        return transcriptToText(doc, messages)
      },
    },
    // RFC 0311 S7: only a host that knows which session it serves can offer
    // it, because the one session it names is that one.
    ...(selfSessionId
      ? {
          name_session: nameSessionTool(repo, projectId, selfSessionId),
          // RFC 0362 3c: the agent adds a project to its own session.
          ...sessionProjectTools(repo, projectId, selfSessionId, opts.worldId),
        }
      : {}),
  }
}

/**
 * Add or remove a project from THIS session (RFC 0362 3c, S5). Its
 * instructions, documents and sources reach the next turn. No session id
 * argument: the session is the one the agent runs in. Adding never changes
 * which project lists the session or who can open it.
 */
function sessionProjectTools(
  repo: Repo,
  homeProjectId: string,
  sessionId: string,
  worldId?: string | null,
): Record<'add_project' | 'remove_project', ToolDef> {
  const projectArg: ToolDef['inputSchema'] = {
    type: 'object',
    properties: {
      project: {
        type: 'string',
        description:
          'The project id (from search with kind "project", or the World tree).',
      },
      why: {
        type: 'string',
        description: 'One line for the thread: why the work needs it.',
      },
    },
    required: ['project'],
  }
  const idOf = (a: Record<string, unknown>) =>
    String(toolArgString(a.project) ?? '')
      .trim()
      .replace(/^automerge:/, '')
  return {
    add_project: {
      description:
        'Add a project to THIS session when the work reaches into it: from ' +
        'the next turn you get its instructions (AGENTS.md), and its ' +
        'repositories are folders you can read and change. Its documents ' +
        "you reach by id, as any project's, passing its project id where a " +
        'tool takes one. Say in your reply which project you added and ' +
        'why; the person can remove it from the session header. Does not ' +
        'move the session or share it with anyone.',
      inputSchema: projectArg,
      execute: async (a) => {
        const id = idOf(a)
        if (!id) throw new Error('project is required: a project id')
        const handle = await repo.find<ProjectDoc>(id as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        const doc = handle.doc() as Record<string, unknown> | undefined
        if (!doc || !('sessionIds' in doc || 'documents' in doc))
          throw new Error(`${id} is not a project`)
        // Only a project in this World (RFC 0362 3e): an id seen in a message
        // is not enough to bring a foreign project's instructions in.
        if (worldId && !(await worldListsProject(repo, worldId, id)))
          throw new Error(`${id} is not a project in this World`)
        const name = String(doc.title ?? id)
        if (!(await addSessionProject(repo, sessionId, homeProjectId, id)))
          return `${name} is already in this session`
        return `added ${name} (${id}); its instructions, documents and sources reach your next turn`
      },
    },
    remove_project: {
      description:
        'Take a project you added back out of THIS session. Its ' +
        'instructions, documents and folders are gone from the next turn.',
      inputSchema: projectArg,
      execute: async (a) => {
        const id = idOf(a)
        if (!id) throw new Error('project is required: a project id')
        return (await removeSessionProject(repo, sessionId, id))
          ? `removed ${id} from this session`
          : `${id} was not added to this session`
      },
    },
  }
}

/**
 * The agent names the session it is running in (RFC 0311 3b, S7).
 *
 * NO SESSION ID ARGUMENT. Naming someone else's session is a rename, and a
 * rename is a person's. The write goes through `nameSessionOnce`, the same
 * stub check the host's namer uses, so it lands only while the title is
 * still the placeholder, and a person's title is never touched.
 */
function nameSessionTool(
  repo: Repo,
  projectId: string,
  sessionId: string,
): ToolDef {
  return {
    description:
      'Give THIS session its name, once, while it still has a placeholder ' +
      'title. Name it like a folder: the feature, product, project or ' +
      'partner the work belongs to, not the task ("session identity", ' +
      '"I&S"). Use a name the conversation already uses, written as it was ' +
      'written. One to three words. Does nothing once the session has a real ' +
      'name, and never renames a title a person chose.',
    inputSchema: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'One to three words, no quotes or full stop.',
        },
      },
      required: ['name'],
    },
    execute: async (a) => {
      const name = cleanTitle(String(toolArgString(a.name) ?? ''))
      if (!name) throw new Error('name is required: one to three words')
      if (await nameSessionOnce(repo, projectId, sessionId, name))
        return `named this session "${name}"`
      const ws = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
      await ready(ws as { whenReady?: () => Promise<void> })
      const entry = ws.doc()?.sessionMeta?.[sessionId]
      if (!entry)
        return 'left as is: this session is not in the project registry'
      const by =
        titleSetBy(entry) === 'you' ? 'named by a person' : 'already named'
      return `left as is: this session is ${by} ("${sessionTitleOf(entry)}")`
    },
  }
}

/**
 * DOCUMENT tools (RFC 0263): create, read, and list a project's documents. A
 * document is a project-scoped Automerge doc, so the agent authors a markdown
 * note or an HTML micro-app and it appears in every client's sidebar.
 *
 * `selfSessionId` is threaded for the reason `commentTools` takes one: a tool
 * call belongs to the session that made it, and RFC 0297 needs to know which
 * session has a document in hand. Absent means nothing is recorded, which is
 * what every caller did before holdings existed.
 */
/**
 * The open comments on one document, as a read hands them over (RFC 0297 §4c).
 *
 * THREADS, NOT A FLAT LIST, for `comment list`'s reason: a reply printed as a
 * peer of the thing it answers reads as two unrelated opinions. Ids are printed
 * because they are what `comment reply` takes, so the agent can answer what it
 * has just been shown without a second lookup.
 *
 * Resolved threads are omitted. A resolved thread is the case where there is
 * nothing here for you, and printing it would put the day's settled argument on
 * top of the document every time anybody read it.
 *
 * BEST EFFORT. A project that will not open costs the reader its comments, not
 * the document it asked for.
 */
async function unresolvedCommentsFor(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<string> {
  try {
    const handle = await repo.find(projectId as AnyDocumentId)
    await ready(handle as { whenReady?: () => Promise<void> })
    // Read off the document's comments (RFC 0339), not the project's map.
    const threads = projectThreads(
      await loadComments(repo, projectId, { documentId }),
      { documentId },
    ).filter((t) => !t.root.resolved)
    if (threads.length === 0) return ''
    const line = (c: CommentEntry, indent: string): string => {
      const anchor = c.target?.anchorText
      return (
        `${indent}${c.id}  ${projectAuthor(handle.doc(), c).label}: ${c.body}` +
        (indent === '' && anchor ? `\n    on: "${anchor}"` : '')
      )
    }
    const body = threads
      .map((t) =>
        [line(t.root, ''), ...t.replies.map((r) => line(r, '  ↳ '))].join('\n'),
      )
      .join('\n')
    const count = threads.length
    return (
      `--- ${count} open comment${count === 1 ? '' : 's'} on this document ---\n` +
      `${body}\n` +
      'Answer one with `comment` (action: reply, parentId: the id above), or ' +
      'resolve it. Reading this is what clears the unread count on your ' +
      'holdings row.'
    )
  } catch {
    return ''
  }
}

/** The largest file `save_file` stores. A file document syncs whole, to every
 *  device in the project, so a scan the size of a film is refused by name. */
export const MAX_FILE_DOCUMENT_BYTES = 20 * 1024 * 1024

/** The mime a saved file opens with, from its extension. */
export function fileMimeFromPath(path: string): string {
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase()
  const known: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
  }
  return known[ext] ?? 'application/octet-stream'
}

/** What `save_file` stores, wherever the bytes came from. */
interface FileToSave {
  name: string
  mime: string
  bytes: Uint8Array
}

export function documentTools(
  repo: Repo,
  projectId: string,
  selfSessionId?: string,
  opts: {
    worldId?: string | null
    /**
     * Read a file on this machine, for `save_file` given a path. A host with
     * a file system passes one (the daemon); without it, `save_file` takes
     * attachments only, which live in the repo. Core never imports `node:fs`.
     */
    readFile?: (path: string) => Promise<Uint8Array>
  } = {},
): Record<string, ToolDef> {
  /**
   * The bytes `save_file` names. An attachment comes off its own document
   * first, so it saves on any host; the attachment's cached copy on disk, or
   * any other absolute path, is read through `readFile` when the host has one.
   */
  const fileToSave = async (file: string): Promise<FileToSave> => {
    const name =
      file
        .split(/[\\/]+/)
        .filter(Boolean)
        .pop() ?? file
    const docId = attachmentDocId(file)
    if (docId) {
      try {
        const handle = await repo.find(docId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        // RFC 0288's `AttachmentDoc`, read loosely: a doc that is not one
        // simply has no bytes here.
        const doc = handle.doc() as
          | { content?: unknown; name?: unknown; mimeType?: unknown }
          | undefined
        if (doc?.content instanceof Uint8Array && doc.content.byteLength) {
          const attached = doc.name ? String(doc.name) : name
          return {
            name: attached,
            mime: doc.mimeType
              ? String(doc.mimeType)
              : fileMimeFromPath(attached),
            bytes: new Uint8Array(doc.content),
          }
        }
      } catch {
        // Not here; the cached copy on disk may still be.
      }
    }
    if (!opts.readFile) {
      throw new Error(
        `"${file}" is not an attachment this host can read, and it has no file system. Pass the attachment path you were handed.`,
      )
    }
    if (!/^([a-zA-Z]:)?[\\/]/.test(file)) {
      throw new Error(`"${file}" is not an absolute path`)
    }
    return {
      name,
      mime: fileMimeFromPath(name),
      bytes: await opts.readFile(file),
    }
  }
  /** RFC 0336 S1. The project that lists a document, so a tool acts where
   *  the document lives rather than where this session was started. */
  const homeOf = (q: RegistryQuery): Promise<string> =>
    registryHolding(repo, projectId, opts.worldId, q)
  /**
   * RFC 0301 §3b's edit check. Read off the document (RFC 0339 3b); the two
   * projects are searched only for a pairing written before the move, which
   * sat on the session's project while the document may live in another, so
   * a refusal in either one stands.
   */
  const sessionProject = projectId
  const editRefusal = async (
    docProject: string,
    documentId: string,
  ): Promise<string | null> =>
    (await mayNotEditReason(repo, docProject, selfSessionId, documentId)) ||
    (docProject !== sessionProject
      ? await mayNotEditReason(repo, sessionProject, selfSessionId, documentId)
      : null) ||
    (await closedBranchRefusal(docProject)) ||
    null
  /**
   * RFC 0301 §3e: the change message an agent write carries, so history can
   * say which session made it and undo can take back just that change. Absent
   * for a host with no session of its own, whose writes have nobody to name.
   */
  const stamp = async (
    a: Record<string, unknown>,
  ): Promise<{ changeMessage?: string }> => {
    if (!selfSessionId) return {}
    // RFC 0313 3a: the turn this write answers, so history can open it.
    let turnId: string | null = null
    try {
      const session = await repo.find(selfSessionId as AnyDocumentId)
      await ready(session as { whenReady?: () => Promise<void> })
      turnId = new BranchLog(session as never).currentTurn()
    } catch {
      // No turn: the change still names its session.
    }
    return {
      changeMessage: changeNote({
        sessionId: selfSessionId,
        ...(turnId ? { turnId: String(turnId) } : {}),
        ...(typeof a.note === 'string' && a.note.trim()
          ? { note: String(a.note).trim().slice(0, 200) }
          : {}),
      }),
    }
  }
  /** The branch this session selected in a project, and its status. */
  const selectedBranch = async (
    docProject: string,
  ): Promise<{ name: string; status: string } | null> => {
    if (!selfSessionId) return null
    try {
      const session = await repo.find(selfSessionId as AnyDocumentId)
      await ready(session as { whenReady?: () => Promise<void> })
      const name = new BranchLog(session as never).branchFor(docProject)
      if (!name) return null
      const project = await repo.find(docProject as AnyDocumentId)
      await ready(project as { whenReady?: () => Promise<void> })
      return {
        name,
        status: findBranch(project.doc(), name)?.status ?? 'missing',
      }
    } catch {
      return null
    }
  }
  /**
   * A write refused because the session's branch closed under it (3j). Once
   * a branch is merged or discarded, falling back to main would land edits the
   * person meant to keep apart, or meant to throw away, where everyone sees
   * them. The agent is told, and chooses with use_branch.
   */
  const closedBranchRefusal = async (
    docProject: string,
  ): Promise<string | null> => {
    const sel = await selectedBranch(docProject)
    if (!sel || sel.status === 'open') return null
    const what =
      sel.status === 'missing' ? 'no longer exists' : `was ${sel.status}`
    return `Your Mod branch "${sel.name}" ${what}, so nothing was written. Call use_branch with an empty branch to work on main, or name an open branch.`
  }
  /**
   * RFC 0313 3j: the open Mod branch this session works on in a project, or ''
   * for main. Read off the session's own log, latest per project. A closed
   * branch reads as '' here, and every write refuses on it first
   * (closedBranchRefusal), so nothing falls through to main unasked.
   */
  /** An optional string argument; String() also unwraps an ImmutableString. */
  const optString = (v: unknown): string | undefined =>
    v == null ? undefined : String(v)
  /**
   * A saved version of a document by name or id (RFC 0313 3c), or the sentence
   * to return when there is none: the versions that do hold it, so the agent
   * can ask again by name.
   */
  const findVersion = async (
    docProject: string,
    documentId: string,
    wanted: string,
  ): Promise<Version | string> => {
    const holding = versionsOf(await listVersions(repo, docProject), documentId)
    const hit = wanted
      ? holding.find((v) => v.id === wanted || v.name === wanted)
      : undefined
    if (hit) return hit
    if (!holding.length) return `No saved versions hold document ${documentId}.`
    const list = holding
      .slice(0, 20)
      .map((v) => `- "${v.name}" (${v.id}), ${new Date(v.at).toISOString()}`)
      .join('\n')
    return `${wanted ? `No version "${wanted}". ` : ''}Versions of ${documentId}, newest first:\n${list}`
  }
  const branchOf = async (docProject: string): Promise<string> => {
    const sel = await selectedBranch(docProject)
    return sel?.status === 'open' ? sel.name : ''
  }
  // RFC 0301 S7: the change id rides on the result, so the thread's card for
  // this edit can offer an undo for exactly this change.
  const changeSuffix = (hash: string | null) =>
    selfSessionId && hash ? ` (change ${hash})` : ''
  /**
   * RFC 0338 S5: the project a move goes to, named the way an agent knows it,
   * by the World's name for it, or by its id. A name matches the World's
   * directory ignoring case; one that matches nothing, or two, is refused
   * rather than guessed.
   */
  const resolveProject = async (
    value: unknown,
  ): Promise<{ id: string; name: string }> => {
    const wanted = String(value ?? '').trim()
    if (!wanted)
      throw new Error('toProject is empty: name the project to move to')
    const world = opts.worldId
      ? await repo.find(opts.worldId as AnyDocumentId)
      : null
    if (world) await ready(world as { whenReady?: () => Promise<void> })
    const projects = projectWorldProjects(world?.doc())
    const byId = projects.find((p) => p.projectId === wanted)
    if (byId) return { id: byId.projectId, name: byId.displayName }
    const byName = projects.filter(
      (p) => p.displayName.toLowerCase() === wanted.toLowerCase(),
    )
    if (byName.length === 1) {
      const only = byName[0] as (typeof byName)[number]
      return { id: only.projectId, name: only.displayName }
    }
    if (byName.length > 1) {
      throw new Error(
        `${byName.length} projects are called "${wanted}"; pass its id`,
      )
    }
    // With a World, only its projects: an id that is not one of them is a
    // page, a session or another World, never somewhere a document can go.
    // Without one, an id is taken as given and core checks it is a project.
    if (!world && isValidDocumentId(wanted)) {
      return { id: wanted, name: wanted }
    }
    throw new Error(`no project called "${wanted}" in this World`)
  }
  const NOTE = {
    type: 'string',
    description:
      'One line on why you are making this edit. Shown beside the diff, next to the undo.',
  }
  return {
    create_document: {
      description:
        'Create a document in this project. A plain document (markdown or ' +
        'HTML) carries `content`; an app INSTANCE (RFC 0267) carries a ' +
        '`dataType` tag and a `data` object of its fields. The mime is inferred ' +
        'from the path extension (.md / .html) unless given. Returns its id.',
      inputSchema: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Display path, e.g. "notes.md" or "reviews/feat-x"',
          },
          content: {
            type: 'string',
            description: 'Text content, for a plain markdown/HTML document',
          },
          data: {
            type: 'object',
            properties: {},
            description:
              "An app instance's structured fields, for a typed document " +
              "(use with dataType; follows the app skill's schema)",
          },
          dataType: {
            type: 'string',
            description:
              'Tag this document as an instance of an app data type, e.g. "review"',
          },
          title: {
            type: 'string',
            description: 'Optional display title (defaults to the path)',
          },
          mime: {
            type: 'string',
            description:
              'Optional content type; inferred from the path if omitted',
          },
        },
        required: ['path'],
      },
      execute: async (a) => {
        const path = String(a.path ?? '').trim()
        if (!path) throw new Error('path is required')
        const content =
          typeof a.content === 'string' ? a.content : String(a.content ?? '')
        const dataType = String(a.dataType ?? '').trim()
        const data =
          a.data && typeof a.data === 'object'
            ? (a.data as Record<string, unknown>)
            : undefined
        // A typed instance defaults to the instance mime; a plain doc infers from
        // the path.
        const mime =
          String(a.mime ?? '').trim() ||
          (dataType ? 'application/mod-instance' : mimeFromPath(path))
        const rawTitle = typeof a.title === 'string' ? a.title.trim() : ''
        const closed = await closedBranchRefusal(projectId)
        if (closed) return closed
        const opts = {
          path,
          mime,
          content,
          ...(rawTitle ? { title: rawTitle } : {}),
          ...(dataType ? { dataType } : {}),
          ...(data ? { data } : {}),
          // RFC 0313 3a: the first change names the session that made it.
          ...(await stamp(a)),
        }
        // RFC 0313 3j: made on a branch, it stays off main's catalog until the
        // branch merges.
        const branch = await branchOf(projectId)
        const documentId = branch
          ? await createOnBranch(repo, projectId, branch, opts)
          : ((await createDocument(repo, projectId, opts)).documentId as string)
        await recordTouch(repo, selfSessionId, documentId, 'write')
        return branch
          ? `Created document ${documentId} (${path}) on branch ${branch}; main lists it when the branch merges.`
          : `Created document ${documentId} (${path})`
      },
    },
    save_file: {
      description:
        'Save a file into this project as a document, so it opens in Mod for ' +
        'everyone: a PDF or an image. Pass a message attachment as the path ' +
        'you were handed (…/mod-attachments/<id>/<name>), or an absolute path ' +
        'to a file on this machine. Returns the new document id; open it with ' +
        'set_view.',
      inputSchema: {
        type: 'object',
        properties: {
          file: {
            type: 'string',
            description:
              'The attachment path or id, or an absolute path on this machine',
          },
          path: {
            type: 'string',
            description:
              'Display path in the project, e.g. "research/sedimentree.pdf". Defaults to the file name.',
          },
          title: {
            type: 'string',
            description: 'Optional display title (defaults to the file name)',
          },
        },
        required: ['file'],
      },
      execute: async (a) => {
        const file = String(a.file ?? '').trim()
        if (!file) throw new Error('file is required')
        const closed = await closedBranchRefusal(projectId)
        if (closed) return closed
        const saved = await fileToSave(file)
        if (saved.bytes.byteLength > MAX_FILE_DOCUMENT_BYTES) {
          const mb = (saved.bytes.byteLength / 1024 / 1024).toFixed(1)
          return `${saved.name} is ${mb} MB; a file document can be at most ${MAX_FILE_DOCUMENT_BYTES / 1024 / 1024} MB, because it syncs whole to every device.`
        }
        const path = normalizeDocumentPath(
          typeof a.path === 'string' && a.path.trim() ? a.path : saved.name,
        )
        if (!path) throw new Error(`"${String(a.path)}" is not a usable path`)
        const rawTitle = typeof a.title === 'string' ? a.title.trim() : ''
        const docOpts = {
          path,
          mime: saved.mime,
          bytes: saved.bytes,
          title: rawTitle || saved.name,
          ...(await stamp(a)),
        }
        const branch = await branchOf(projectId)
        const documentId = branch
          ? await createOnBranch(repo, projectId, branch, docOpts)
          : ((await createDocument(repo, projectId, docOpts))
              .documentId as string)
        await recordTouch(repo, selfSessionId, documentId, 'write')
        const kb = Math.max(1, Math.round(saved.bytes.byteLength / 1024))
        return `Saved ${saved.name} (${saved.mime}, ${kb} KB) as document ${documentId} (${path})${branch ? ` on branch ${branch}` : ''}`
      },
    },
    list_documents: {
      description:
        'List the documents in this project, as "id  path  (mime)  title" lines.',
      inputSchema: { type: 'object', properties: {} },
      execute: async () => {
        const handle = await repo.find(projectId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        // RFC 0313 3j: on a branch, what the branch has: main's documents, less
        // what it removed, plus what it made.
        const branch = await branchOf(projectId)
        let docs = projectDocuments(handle.doc())
        if (branch) {
          const made = new Map<string, Record<string, unknown>>()
          for (const id of findBranch(handle.doc(), branch)?.created ?? []) {
            const h = await repo.find(id as AnyDocumentId)
            await ready(h as { whenReady?: () => Promise<void> })
            made.set(id, (h.doc() ?? {}) as Record<string, unknown>)
          }
          docs = branchDocuments(handle.doc(), branch, made)
        }
        if (docs.length === 0) return '(no documents)'
        return docs
          .map((d) => `${d.id}  ${d.path}  (${d.mime})  ${d.title}`)
          .join('\n')
      },
    },
    read_document: {
      description:
        'Read a document, given its id. For a plain document returns its text ' +
        'content; for an app INSTANCE (a document with a dataType) returns its ' +
        'structured fields as JSON.',
      inputSchema: {
        type: 'object',
        properties: {
          documentId: {
            type: 'string',
            description: 'The document id to read',
          },
          version: {
            type: 'string',
            description:
              'Read it as a saved version had it (RFC 0313), by the version name or id, without restoring anything',
          },
        },
        required: ['documentId'],
      },
      execute: async (a) => {
        const documentId = String(a.documentId ?? a.document_id ?? '').trim()
        if (!documentId) throw new Error('documentId is required')
        // A Mod skill (RFC 0334 3b) ships in core and has no document on the
        // log; the listed block names it `mod:<skillId>`, and it reads here.
        const modSkill = modSkillText(documentId)
        if (modSkill !== null) return modSkill
        // RFC 0336 S1: the comments come from the document's own project.
        const projectId = await homeOf({ documentId })
        const handle = await repo.find(documentId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        // RFC 0313 3c: a saved version reads as it was, and nothing changes.
        const wanted = optString(a.version)?.trim()
        if (wanted) {
          const version = await findVersion(projectId, documentId, wanted)
          if (typeof version === 'string') return version
          const then = (await readAtVersion(repo, version, documentId)) as {
            dataType?: unknown
          }
          return then && typeof then.dataType === 'string' && then.dataType
            ? JSON.stringify(readDocumentData(then), null, 2)
            : readDocumentContent(then)
        }
        // RFC 0313 3j: on a branch, read what the branch reads.
        const branch = await branchOf(projectId)
        const doc = (
          branch
            ? await readOnBranch(repo, projectId, branch, documentId)
            : handle.doc()
        ) as { dataType?: unknown } | undefined
        // A typed instance carries BOTH: structured fields, and a body (RFC
        // 0271) for the prose that belongs to the record itself — a review's
        // plan lives here rather than in a second document that has to be
        // named and kept in step. Returning only the fields hid the body from
        // every agent, which is why a plan could not be migrated into its own
        // review. Body omitted when empty, so an instance that has none reads
        // exactly as it did.
        await recordTouch(repo, selfSessionId, documentId, 'read')
        // The unresolved comments come WITH the read (RFC 0297 §4c). A read is
        // what clears the bar's comment count, so a read that did not hand the
        // comments over cleared a badge for words nobody had seen. They ride on
        // the content rather than in a second tool because the question "what
        // does this document say" and "what has somebody asked about it" are one
        // question for whoever is about to answer.
        const comments = await unresolvedCommentsFor(
          repo,
          projectId,
          documentId,
        )
        if (doc && typeof doc.dataType === 'string' && doc.dataType) {
          const body = readDocumentContent(doc)
          const fields = JSON.stringify(readDocumentData(doc), null, 2)
          const head = body ? `${fields}\n\n--- body ---\n${body}` : fields
          return comments ? `${head}\n\n${comments}` : head
        }
        // A file has no text to hand back; say what it is rather than ''.
        const bytes = readDocumentBytes(doc)
        const content = bytes
          ? `A file document: ${String((doc as { mime?: unknown }).mime ?? '')}, ${bytes.byteLength} bytes. It has no text content here; it opens in the window.`
          : readDocumentContent(doc)
        return comments ? `${content}\n\n${comments}` : content
      },
    },
    save_version: {
      description:
        'Save a named version (RFC 0313) of a document, or of the whole project ' +
        'when no documentId is given. It stores no copy, only where each ' +
        'document stood, and can be read, restored or opened as a copy later. ' +
        'Save one before trying a different direction.',
      inputSchema: {
        type: 'object',
        properties: {
          name: { type: 'string', description: 'What to call it' },
          documentId: {
            type: 'string',
            description: 'The document; omit for the whole project',
          },
          note: { type: 'string', description: 'Why, in a line' },
        },
        required: ['name'],
      },
      execute: async (a) => {
        const name = optString(a.name)?.trim()
        if (!name) throw new Error('name is required')
        const documentId = optString(a.documentId)?.trim()
        const home = documentId ? await homeOf({ documentId }) : sessionProject
        const note = optString(a.note)?.trim()
        const version = await saveVersion(repo, home, {
          name,
          by: selfSessionId || 'agent',
          covers: documentId
            ? { kind: 'document', documentId }
            : { kind: 'project' },
          ...(note ? { note } : {}),
        })
        return `Saved version "${version.name}" (${version.id}) of ${documentId ? `document ${documentId}` : 'the project'}, ${Object.keys(version.heads).length} document(s).`
      },
    },
    restore_version: {
      description:
        'Put a document back to a saved version (RFC 0313), as one new change. ' +
        'What it holds now is saved as a version first, so nothing is lost. ' +
        'Omit `version` to list the versions that hold the document.',
      inputSchema: {
        type: 'object',
        properties: {
          documentId: { type: 'string', description: 'The document' },
          version: {
            type: 'string',
            description: 'The version name or id; omit to list them',
          },
        },
        required: ['documentId'],
      },
      execute: async (a) => {
        const documentId = optString(a.documentId)?.trim()
        if (!documentId) throw new Error('documentId is required')
        const home = await homeOf({ documentId })
        const refused = await editRefusal(home, documentId)
        if (refused) return refused
        if (await branchOf(home)) {
          return 'Restore works on main. Call use_branch with an empty branch first, or open the version as a copy. Nothing was written.'
        }
        const wanted = optString(a.version)?.trim()
        const version = await findVersion(home, documentId, wanted ?? '')
        if (typeof version === 'string') return version
        const out = await restoreVersion(
          repo,
          home,
          documentId,
          version.heads[documentId] ?? [],
          selfSessionId
            ? { by: { id: selfSessionId, sessionId: selfSessionId } }
            : {},
        )
        if (!out.ok) return `Could not restore: ${out.reason}.`
        await recordTouch(repo, selfSessionId, documentId, 'write')
        return `Restored document ${documentId} to "${version.name}". What it held before is saved as the version "Before restore".`
      },
    },
    read_history: {
      description:
        "Read a document's history (RFC 0313): who changed what and when, " +
        'newest first, people and agents alike, with the note an agent left and ' +
        'the turn (prompt or comment) its change answered. Pass `passage` to ' +
        'see only changes that touched that text. Use it to answer what changed ' +
        'and why, instead of reading the document twice.',
      inputSchema: {
        type: 'object',
        properties: {
          documentId: { type: 'string', description: 'The document' },
          passage: {
            type: 'string',
            description: 'Only changes whose text includes this',
          },
          limit: {
            type: 'number',
            description: 'How many rows, newest first (default 20)',
          },
        },
        required: ['documentId'],
      },
      execute: async (a) => {
        const documentId = String(a.documentId ?? '').trim()
        if (!documentId) throw new Error('documentId is required')
        const handle = await repo.find(documentId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        const passage = typeof a.passage === 'string' ? a.passage.trim() : ''
        const limit = Math.max(1, Math.min(200, Number(a.limit) || 20))
        // Read more than asked when filtering, so a passage still finds rows.
        const rows = projectChanges(handle.doc(), {
          limit: passage ? limit * 10 : limit,
        }).changes.filter(
          (r) =>
            !passage ||
            [
              r.text?.added,
              r.text?.removed,
              r.text?.contextBefore,
              r.text?.contextAfter,
            ].some((t) => t?.includes(passage)),
        )
        if (!rows.length) return 'No changes recorded.'
        const clip = (t: string) => {
          const one = t.replace(/\s+/g, ' ').trim()
          return one.length > 120 ? `${one.slice(0, 119)}…` : one
        }
        return rows
          .slice(0, limit)
          .map((r) => {
            const who = r.sessionId
              ? `session ${r.sessionId}`
              : r.authorId
                ? `person ${r.authorId}`
                : 'unattributed'
            const what = r.text
              ? [
                  r.text.removed ? `removed "${clip(r.text.removed)}"` : '',
                  r.text.added ? `added "${clip(r.text.added)}"` : '',
                ]
                  .filter(Boolean)
                  .join(', ')
              : (r.fields ?? []).map((f) => f.path).join(', ')
            return [
              new Date(r.time * 1000).toISOString(),
              who,
              what,
              r.note ? `note: ${r.note}` : '',
              r.turnId ? `turn ${r.turnId}` : '',
              r.reverted ? 'undone' : '',
              r.reverts ? `undoes ${r.reverts}` : '',
              r.restores ? 'restore' : '',
              `change ${r.hash}`,
            ]
              .filter(Boolean)
              .join(' · ')
          })
          .join('\n')
      },
    },
    update_document: {
      description:
        'Update an existing document IN PLACE, given its id — do NOT create a ' +
        'copy. For a plain document pass `content` (the full new text). For an ' +
        'app INSTANCE (a document with a dataType) pass `data` (the complete new ' +
        'fields object); it replaces the instance state. Pass `title` to rename ' +
        'it, and `path` to move it to another folder (same id, so links, ' +
        'comments and open panes follow), alone or alongside a write.',
      inputSchema: {
        type: 'object',
        properties: {
          documentId: {
            type: 'string',
            description: 'The document id to update',
          },
          title: {
            type: 'string',
            description:
              'A new title for the document, as the sidebar shows it. On its own this is a rename and the content is untouched.',
          },
          path: {
            type: 'string',
            description:
              'A new display path, e.g. "funding/launch-plan": the folder is everything before the last "/". On its own this is a move and the content is untouched. Refused if another document already has that path.',
          },
          toProject: {
            type: 'string',
            description:
              'Move the document to ANOTHER project, by its name in the World or its id. Same id, so links, panes and comments follow; with `path` it lands there, otherwise at the path it has now. Pass it on its own, not with content, data or title.',
          },
          force: {
            type: 'boolean',
            description:
              'Write over BUILD OUTPUT (RFC 0274) on purpose, converting it back to an ordinary document. Without this, a generated document refuses a prose write so a build is not silently discarded.',
          },
          content: {
            type: 'string',
            description: 'The complete new text, for a plain document',
          },
          note: NOTE,
          data: {
            type: 'object',
            properties: {},
            description:
              'The complete new fields object, for an app instance ' +
              '(a document with a dataType)',
          },
        },
        required: ['documentId'],
      },
      execute: async (a) => {
        const documentId = String(a.documentId ?? a.document_id ?? '').trim()
        if (!documentId) throw new Error('documentId is required')
        // RFC 0336 S1: the document's own project, not this session's. The
        // edit check, rename, data write and comments all live there.
        const projectId = await homeOf({ documentId })
        // RFC 0301 §3b: a pairing with editing off refuses, before anything is read.
        const refused = await editRefusal(projectId, documentId)
        if (refused) return refused
        // RFC 0313 3j: on a branch, a content or field write goes to the
        // branch's copy, made on this first write. Main never sees it until
        // the branch merges. A rename or a move is a catalog write on main.
        const branch = await branchOf(projectId)
        // A rename or move is a catalog write on main, and a branch holds no
        // titles of its own yet. Mixed with a content write, the content fell
        // through to main with it (0313 adversarial review, finding 1). So on a
        // branch the two go separately, and this refuses before writing.
        if (
          branch &&
          (a.content !== undefined || a.data !== undefined) &&
          (a.title !== undefined ||
            a.path !== undefined ||
            a.toProject !== undefined)
        ) {
          return `On branch ${branch}, change the content and the title or place in separate calls: the content goes to the branch, a rename or move goes to main. Nothing was written.`
        }
        if (branch && (a.content !== undefined || a.data !== undefined)) {
          const copyId = await copyOnBranch(repo, projectId, branch, documentId)
          const stamped = await stamp(a)
          if (a.data && typeof a.data === 'object') {
            const copy = await repo.find(copyId as AnyDocumentId)
            await ready(copy as { whenReady?: () => Promise<void> })
            copy.change(
              (d: Record<string, unknown>) => {
                d.data = JSON.parse(JSON.stringify(a.data))
                d.updatedAt = Date.now()
              },
              stamped.changeMessage ? { message: stamped.changeMessage } : {},
            )
          }
          if (typeof a.content === 'string') {
            await writeOnBranch(
              repo,
              projectId,
              branch,
              documentId,
              a.content,
              await stamp(a),
            )
          }
          await recordTouch(repo, selfSessionId, documentId, 'write')
          return `Updated document ${documentId} on branch ${branch}. Main is unchanged until the branch merges.`
        }
        // A rename is a catalog write, not a content write, so it happens
        // first and on its own terms: `title` with nothing else is a rename
        // and must NOT fall through to the plain-document branch, which
        // would write an empty string over the content.
        const title = String(a.title ?? '').trim()
        const hasWrite = a.content !== undefined || a.data !== undefined
        // RFC 0338 S5: to another project. On its own, because every later
        // write in this call addresses the document's project, which this
        // changes under it.
        if (a.toProject !== undefined) {
          if (title || hasWrite) {
            throw new Error(
              'move to another project on its own, then edit or rename it there',
            )
          }
          const dest = await resolveProject(a.toProject)
          const res = await moveToProject(
            repo,
            projectId,
            dest.id,
            {
              documentId,
              ...(a.path !== undefined ? { path: String(a.path ?? '') } : {}),
            },
            { worldId: opts.worldId },
          )
          await recordTouch(repo, selfSessionId, documentId, 'write')
          return `Moved document ${documentId} to project "${dest.name}"${missingAppsNote(res.missingApps)}`
        }
        // RFC 0338 3d: a move is a catalog write too, and goes first, so a
        // refused move (a taken path, a blank one) writes nothing else. A path
        // that was PASSED is a move however it reads: treating a blank one as
        // absent let the call fall through to a content write and erase the
        // document (0338 adversarial review, finding 1).
        const moved =
          a.path !== undefined
            ? (
                await moveDocument(
                  repo,
                  projectId,
                  documentId,
                  String(a.path ?? ''),
                )
              ).path
            : ''
        if (!title && !moved && !hasWrite) {
          throw new Error(
            'nothing to update: pass data for the fields or content for the body, title to rename, or path to move',
          )
        }
        if (title) await renameDocument(repo, projectId, documentId, title)
        if ((title || moved) && !hasWrite) {
          await recordTouch(repo, selfSessionId, documentId, 'write')
          if (!moved) return `Renamed document ${documentId} to "${title}"`
          return `Moved document ${documentId} to ${moved}${title ? ` and renamed to "${title}"` : ''}`
        }
        const renamed =
          (moved ? ` and moved to ${moved}` : '') +
          (title ? ` and renamed to "${title}"` : '')
        // Route by the TARGET's kind, so the agent can't corrupt an instance by
        // writing text into its content (or vice versa). A typed instance
        // updates `data`; a plain document updates `content`.
        const handle = await repo.find(documentId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        const doc = handle.doc() as { dataType?: unknown } | undefined
        const isInstance = !!(
          doc &&
          typeof doc.dataType === 'string' &&
          doc.dataType
        )
        if (isInstance) {
          // An instance has FIELDS and a BODY (RFC 0271), and either may be
          // written. `data` sets the fields; `content` sets the body, which is
          // where a review's plan lives now that it is not a separate document.
          //
          // The old fallback PARSED content as JSON and wrote it to the fields,
          // which was reasonable when a body did not exist and is now actively
          // wrong: markdown starting with "{" would land in data, and every
          // other body would throw. Content means body here, full stop.
          const data =
            a.data && typeof a.data === 'object'
              ? (a.data as Record<string, unknown>)
              : undefined
          const body = typeof a.content === 'string' ? a.content : undefined
          if (!data && body === undefined) {
            throw new Error(
              `${documentId} is an app instance (dataType "${String(doc?.dataType)}"); pass \`data\` for its fields, \`content\` for its body, or both`,
            )
          }
          // Stamped like the body, so a record change names its session and
          // turn (0313 adversarial review, finding 8).
          if (data)
            await writeDocumentData(
              repo,
              projectId,
              documentId,
              data,
              await stamp(a),
            )
          // Merged, not replaced: writeDocumentContent splices via updateText, so a
          // concurrent edit to another part of the plan survives this write.
          if (body !== undefined) {
            await writeDocumentContent(
              repo,
              projectId,
              documentId,
              body,
              await stamp(a),
            )
          }
          const wrote = [
            data ? 'fields' : null,
            body !== undefined ? 'body' : null,
          ]
            .filter(Boolean)
            .join(' + ')
          await recordTouch(repo, selfSessionId, documentId, 'write')
          return `Updated instance ${documentId} (${wrote})${renamed}`
        }
        // The refusal lives in writeDocumentContent, so every write path inherits it.
        // This forwards the deliberate conversion, and improves the message:
        // only the tool knows it has a `force` parameter to name.
        const content =
          typeof a.content === 'string' ? a.content : String(a.content ?? '')
        let changed: string | null = null
        try {
          changed = await writeDocumentContent(
            repo,
            projectId,
            documentId,
            content,
            {
              overwriteGenerated: a.force === true,
              ...(await stamp(a)),
            },
          )
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          if (/build output/i.test(msg)) {
            return `${msg} Pass force: true to convert it back on purpose.`
          }
          throw e
        }
        await recordTouch(repo, selfSessionId, documentId, 'write')
        return `Updated document ${documentId}${changeSuffix(changed)}${renamed}`
      },
    },
    patch_document: {
      description:
        'Change PART of a plain document by exact string replacement, leaving ' +
        'the rest untouched. Prefer this over update_document for a targeted ' +
        'edit: update_document replaces the whole content, so the model must ' +
        'reproduce the entire file and can silently alter parts it never meant ' +
        'to touch. `oldString` must appear exactly once unless replaceAll is set.',
      inputSchema: {
        type: 'object',
        properties: {
          documentId: {
            type: 'string',
            description: 'The document id to patch',
          },
          oldString: {
            type: 'string',
            description: 'Exact text to replace, including indentation',
          },
          newString: { type: 'string', description: 'What to replace it with' },
          replaceAll: {
            type: 'boolean',
            description:
              'Replace every occurrence instead of requiring exactly one',
          },
          note: NOTE,
        },
        required: ['documentId', 'oldString', 'newString'],
      },
      execute: async (a) => {
        const documentId = String(a.documentId ?? '')
        const oldString = String(a.oldString ?? '')
        const newString = String(a.newString ?? '')
        const replaceAll = a.replaceAll === true
        if (!documentId || !oldString)
          return 'documentId and oldString required'
        // RFC 0336 S1: the document's own project, not this session's.
        const projectId = await homeOf({ documentId })
        const refused = await editRefusal(projectId, documentId)
        if (refused) return refused

        const handle = await repo.find(documentId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        const doc = handle.doc()
        const dataType = (doc as { dataType?: unknown })?.dataType
        // An instance's BODY is text and patches like any other document (RFC
        // 0271). Only refuse when there is no body to patch, and say which
        // tool writes the fields, since that is the likely confusion.
        if (
          typeof dataType === 'string' &&
          dataType &&
          !readDocumentContent(doc)
        ) {
          return `Document ${documentId} is an app instance ("${dataType}") with an empty body: its FIELDS are structured data. Use update_document with \`data\` for fields, or \`content\` to start a body.`
        }

        // RFC 0313 3j: on a branch, patch the text the branch reads.
        const branch = await branchOf(projectId)
        const before = branch
          ? readDocumentContent(
              await readOnBranch(repo, projectId, branch, documentId),
            )
          : readDocumentContent(doc)
        const count = before.split(oldString).length - 1
        if (count === 0) {
          return 'Not found. oldString must match EXACTLY, including indentation and line breaks — read_document and copy the text verbatim.'
        }
        if (count > 1 && !replaceAll) {
          return `Found ${count} occurrences. Include more surrounding context to make oldString unique, or pass replaceAll: true.`
        }

        // Literal, NEVER String.replace: with a string pattern it still expands
        // `$&`, `` $` ``, `$'` and `$$` in the REPLACEMENT, so patching a doc
        // containing shell, sed, or LaTeX silently corrupts it while reporting
        // success. split/join is literal and matches the replaceAll branch, so
        // the same input cannot behave differently depending on the flag.
        const after = replaceAll
          ? before.split(oldString).join(newString)
          : (() => {
              const at = before.indexOf(oldString)
              return (
                before.slice(0, at) +
                newString +
                before.slice(at + oldString.length)
              )
            })()

        if (branch) {
          await writeOnBranch(
            repo,
            projectId,
            branch,
            documentId,
            after,
            await stamp(a),
          )
          await recordTouch(repo, selfSessionId, documentId, 'write')
          return `Patched document ${documentId} on branch ${branch} — ${count} replacement${count === 1 ? '' : 's'}. Main is unchanged until the branch merges.`
        }
        const changed = await writeDocumentContent(
          repo,
          projectId,
          documentId,
          after,
          await stamp(a),
        )
        await recordTouch(repo, selfSessionId, documentId, 'write')
        return `Patched document ${documentId} — ${count} replacement${count === 1 ? '' : 's'}, ${before.length} -> ${after.length} chars${changeSuffix(changed)}`
      },
    },
    use_branch: {
      description:
        'Work on a Mod branch (RFC 0313): your document writes in this project ' +
        'go to the branch, copied on first write, and main is unchanged until ' +
        'someone merges it. Pass a new name to open a branch from main, an open ' +
        "branch's name to join it, or an empty name to go back to main. Ask the " +
        'person first before changing more than five documents on main.',
      inputSchema: {
        type: 'object',
        properties: {
          branch: {
            type: 'string',
            description: 'The branch name, or empty for main',
          },
        },
        required: ['branch'],
      },
      execute: async (a) => {
        if (!selfSessionId)
          return 'This host has no session to put on a branch.'
        const name = String(a.branch ?? '').trim()
        if (name) {
          const project = await repo.find(projectId as AnyDocumentId)
          await ready(project as { whenReady?: () => Promise<void> })
          if (findBranch(project.doc(), name)?.status !== 'open') {
            await createBranch(repo, projectId, { name, by: selfSessionId })
          }
        }
        const session = await repo.find(selfSessionId as AnyDocumentId)
        await ready(session as { whenReady?: () => Promise<void> })
        new BranchLog(session as never).appendBranch({
          projectId,
          branch: name,
        })
        return name
          ? `Working on branch ${name}. Document writes go to the branch; main is unchanged until it merges.`
          : 'Working on main.'
      },
    },
    delete_document: {
      description:
        'Remove a document from this project, given its id (from ' +
        'list_documents). Unlinks it from the sidebar; use when a document is ' +
        'obsolete or was created by mistake.',
      inputSchema: {
        type: 'object',
        properties: {
          documentId: {
            type: 'string',
            description: 'The document id to remove',
          },
        },
        required: ['documentId'],
      },
      execute: async (a) => {
        const documentId = String(a.documentId ?? a.document_id ?? '').trim()
        if (!documentId) throw new Error('documentId is required')
        // RFC 0336 S1: the document's own project, not this session's. The
        // edit check, rename, data write and comments all live there.
        const projectId = await homeOf({ documentId })
        const refused = await editRefusal(projectId, documentId)
        if (refused) return refused
        // RFC 0313 3j: on a branch, main keeps it until the branch merges.
        const branch = await branchOf(projectId)
        if (branch) {
          await deleteOnBranch(repo, projectId, branch, documentId)
          return `Removed document ${documentId} on branch ${branch}; main keeps it until the branch merges.`
        }
        await deleteDocument(repo, projectId, documentId)
        return `Removed document ${documentId}`
      },
    },
    make_project: {
      description:
        'Turn a folder of documents into a nested project. Documents keep their ids, comments, and links; sessions and tasks remain in this project.',
      inputSchema: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'The folder to make a project' },
        },
        required: ['path'],
      },
      execute: async (a) => {
        if (!opts.worldId) throw new Error('this project has no World')
        const path = normalizeDocumentPath(String(a.path ?? ''))
        if (!path) throw new Error('path is required')
        if (await branchOf(projectId))
          throw new Error('switch to main before making a project')
        const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
        await ready(project as { whenReady?: () => Promise<void> })
        for (const doc of projectDocuments(project.doc())) {
          if (
            !(normalizeDocumentPath(String(doc.path)) ?? '').startsWith(
              `${path}/`,
            )
          )
            continue
          const refused = await mayNotEditReason(
            repo,
            projectId,
            selfSessionId,
            doc.id,
          )
          if (refused) return refused
        }
        const created = await makeProject(repo, opts.worldId, projectId, path)
        return `Made ${path} a project (${created}); its documents kept their ids and comments.`
      },
    },
    move_folder: {
      description:
        'Move or rename a FOLDER of documents (RFC 0338): every document whose ' +
        'path starts with `from/` now starts with `to/`. "funding" to ' +
        '"fundraise" renames it; "funding" to "company/funding" moves it. Ids ' +
        'do not change. All or nothing: if any new path is taken, nothing ' +
        'moves. To move ONE document, use update_document with `path`. ' +
        'With `toProject` the folder moves to ANOTHER project instead, ' +
        'under `to` there (its own name when `to` is absent).',
      inputSchema: {
        type: 'object',
        properties: {
          from: {
            type: 'string',
            description: 'The folder to move, e.g. "timeline"',
          },
          to: {
            type: 'string',
            description: 'Where it goes, e.g. "funding/plans"',
          },
          toProject: {
            type: 'string',
            description:
              'Move the folder to another project, by its name in the World or its id.',
          },
        },
        required: ['from'],
      },
      execute: async (a) => {
        const from = normalizeDocumentPath(String(a.from ?? ''))
        const crossing = a.toProject !== undefined
        const to =
          a.to === undefined && crossing
            ? from
            : normalizeDocumentPath(String(a.to ?? ''))
        if (!from || !to) throw new Error('from and to are both required')
        // A pairing with editing off refuses for any document the move touches.
        const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
        await ready(project as { whenReady?: () => Promise<void> })
        const under = projectDocuments(project.doc()).filter((d) =>
          (normalizeDocumentPath(String(d.path)) ?? '').startsWith(`${from}/`),
        )
        for (const d of under) {
          const refused = await mayNotEditReason(
            repo,
            projectId,
            selfSessionId,
            d.id,
          )
          if (refused) return refused
        }
        // Move exactly what was checked: a document filed into the folder
        // while the checks awaited was never checked, and refuses the move.
        if (crossing) {
          const dest = await resolveProject(a.toProject)
          const res = await moveToProject(
            repo,
            projectId,
            dest.id,
            { folder: from, to },
            { expectIds: under.map((d) => d.id), worldId: opts.worldId },
          )
          for (const id of res.moved) {
            await recordTouch(repo, selfSessionId, id, 'write')
          }
          return `Moved ${res.moved.length} document${res.moved.length === 1 ? '' : 's'} from ${from}/ to project "${dest.name}" as ${to}/${missingAppsNote(res.missingApps)}`
        }
        const { moved } = opts.worldId
          ? await moveFolderWithProjects(
              repo,
              opts.worldId,
              projectId,
              from,
              to,
              {
                expectIds: under.map((d) => d.id),
              },
            )
          : await moveFolder(repo, projectId, from, to, {
              expectIds: under.map((d) => d.id),
            })
        if (moved.length === 0) return `${from} is already at ${to}`
        for (const id of moved) {
          await recordTouch(repo, selfSessionId, id, 'write')
        }
        return `Moved ${moved.length} document${moved.length === 1 ? '' : 's'} from ${from}/ to ${to}/`
      },
    },
  }
}

/**
 * Every field a comment can be anchored by (RFC 0289 &sect;5).
 *
 * ONE list, used to filter both what `create` writes and what `list` matches on,
 * because the two must agree: a field the agent can write but not query is a
 * comment it can place and never find again. The three that used to be here
 * (`documentId`, `sessionId`, `messageId`) were the whole vocabulary, so an
 * agent reviewing a diff could not say WHICH LINE it meant and RFC 0285's
 * staleness marker never fired on an agent's comment at all.
 */
const TARGET_FIELDS = [
  'documentId',
  'sessionId',
  'messageId',
  'branch',
  'path',
  'hunk',
  'commit',
  'anchorText',
  'nodeId',
] as const

/**
 * Turn the tool's `{ kind, id, label }` mentions into stored chips (RFC 0292 S4).
 *
 * THE AGENT NAMES A LABEL, NOT AN OFFSET. Asking a model to count UTF-16
 * characters is asking for a chip on the wrong words, so the label is touched up
 * in the body and the offsets come from the text itself. That also enforces the
 * invariant from the other direction: a mention the body does not show is a
 * comment that wakes a session for a reason nobody reading it can see.
 *
 * THROWS on a label that is absent or appears twice, rather than guessing. The
 * agent wrote both halves and can fix either, and an error it can act on beats a
 * mention silently attached to the wrong occurrence.
 */
function resolveToolMentions(raw: unknown, body: string): Mention[] {
  if (!Array.isArray(raw) || raw.length === 0) return []
  const out: Mention[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as { kind?: unknown; id?: unknown; label?: unknown }
    const kind: MentionKind =
      e.kind === 'document'
        ? 'document'
        : e.kind === 'task'
          ? 'task'
          : 'session'
    const id = String(e.id ?? '').trim()
    const label = String(e.label ?? '').trim()
    if (!id) throw new Error('each mention needs an id')
    if (!label)
      throw new Error(`mention ${id} needs a label to show in the body`)
    const chip = `@${label}`
    const at = body.indexOf(chip)
    if (at < 0) {
      throw new Error(
        `the body must contain "${chip}" for the mention of ${id}: a mention ` +
          'the reader cannot see is a session woken for no visible reason',
      )
    }
    if (body.indexOf(chip, at + 1) >= 0) {
      throw new Error(
        `"${chip}" appears more than once in the body, so the mention of ${id} ` +
          'is ambiguous: make each name unique or drop the mention',
      )
    }
    out.push({ kind, id, at, len: chip.length })
  }
  return out
}

/** What the tool says about who was woken, appended to its result line. */
function describeDelivery(out: DeliverMentionsResult): string {
  if (out.delivered.length === 0) return ''
  const woken = out.delivered.filter((d) => d.woken).map((d) => d.sessionId)
  const recorded = out.delivered.filter((d) => !d.woken).map((d) => d.sessionId)
  const parts: string[] = []
  if (woken.length > 0) parts.push(`woke ${woken.join(', ')}`)
  // A captured session is TOLD and not run: saying so keeps "nothing happened"
  // distinguishable from "its responder is not ours to queue for".
  if (recorded.length > 0) {
    parts.push(`told ${recorded.join(', ')} (captured, no run)`)
  }
  return parts.length > 0 ? ` (${parts.join('; ')})` : ''
}

/**
 * COMMENT tool (RFC 0267 &sect;4a, RFC 0289 S2): the core annotation interface as
 * ONE tool with an `action` verb (create / reply / resolve / delete / list),
 * rather than five, to keep the project toolset small — the same tool-count
 * discipline that dropped list_apps. A comment is anchored to a `target` (a
 * document or app instance by documentId; a session or one message by
 * sessionId/messageId; a line of a diff by branch/path/hunk), so "resolve the
 * comment on line 40" is the same operation the bridge exposes to a view.
 *
 * The agent is the author here (no per-call identity flows into a tool yet), so
 * comments it creates are attributed to `author` (default "agent"); a human's
 * comments come through the bridge with the user's identity instead.
 *
 * `sessionId` is what makes a comment ANSWERABLE. Recorded on everything this
 * toolset writes, so when somebody replies later there is a session to wake
 * (RFC 0289). Optional because not every host has one in hand — the cli builds
 * this toolset outside any session — and a comment without it is exactly what
 * comments were before: readable, and unable to reach anyone.
 */
export function commentTools(
  repo: Repo,
  projectId: string,
  sessionId?: string,
  opts: {
    /** The World, so a comment on a page in ANOTHER project can be found
     *  and answered (S11). Without it the tool acts in the home registry. */
    worldId?: string | null
  } = {},
): Record<string, ToolDef> {
  // The registry a call acts in, resolved from what it names: the comment, or
  // the document. Home nearly always; a page from another project otherwise.
  const registryFor = (q: RegistryQuery) =>
    registryHolding(repo, projectId, opts.worldId, q)
  // The page a target is on, or the session when it names no page: the
  // project that indexes its comments (RFC 0339 3d).
  const subjectQuery = (target: Record<string, string>): RegistryQuery =>
    target.documentId
      ? { documentId: target.documentId }
      : target.sessionId
        ? { sessionId: target.sessionId }
        : {}
  return {
    comment: {
      description:
        'Comment on something in this project (RFC 0267, RFC 0289). ' +
        '`action`: "create" (needs `target` + `body`), "reply" (needs `id` + ' +
        '`body`), "resolve" (needs `id`), "delete" (needs `id`), or "list" ' +
        '(optional `target` to filter). A `target` anchors the comment: ' +
        '{ documentId } for a document or app instance, { sessionId, ' +
        'messageId? } for a session or one of its messages, or { documentId, ' +
        'branch, path, hunk, commit, anchorText } for a line of a diff on a ' +
        'review document. Listing with just ' +
        '{ sessionId } returns every comment in that session, and list returns ' +
        'THREADS with replies indented under their root. On a document also ' +
        'pass target.anchorText quoting the passage you mean, or the comment ' +
        'says only "somewhere on this document"; on a document with no prose ' +
        'to quote (a canvas), target.nodeId names the object instead. ' +
        'Answer an existing comment ' +
        'with "reply" rather than creating a second top-level one: a reply ' +
        'joins that thread and inherits its anchor. Resolve acts on the whole ' +
        'thread whichever id in it you name. Delete removes the text (use it ' +
        'for a comment that should not exist); resolve keeps it. `mentions` ' +
        'names a session or a document from inside the body (RFC 0292): each ' +
        'entry is { kind, id, label } and the body must contain "@label" ' +
        'exactly once, which is the chip a reader sees. NAMING A SESSION WAKES ' +
        'IT, so use it to ask a specific session something instead of writing a ' +
        'comment nobody is watching for; naming a document is only a link.',
      inputSchema: {
        type: 'object',
        properties: {
          action: {
            type: 'string',
            enum: ['create', 'reply', 'resolve', 'delete', 'list'],
            description:
              'What to do: create, reply, resolve, delete, or list comments',
          },
          target: {
            type: 'object',
            properties: {
              documentId: { type: 'string' },
              sessionId: { type: 'string' },
              messageId: { type: 'string' },
              branch: { type: 'string' },
              path: { type: 'string' },
              hunk: { type: 'string' },
              commit: { type: 'string' },
              anchorText: { type: 'string' },
              nodeId: { type: 'string' },
            },
            description:
              'What the comment is on (for create and list): a document/instance ' +
              'by documentId, a session/message by sessionId[/messageId], or a ' +
              'diff line by branch/path/hunk with the documentId of the review ' +
              'it is on. anchorText quotes the exact text ' +
              'the comment is about, so a reader can find it and it reports ' +
              'itself stale once that text changes. nodeId names one object ' +
              'inside a non-prose document (a canvas node, say); pair it with ' +
              'anchorText as the label, and list with it to read only that ' +
              "object's thread",
          },
          body: {
            type: 'string',
            description: 'The comment text (for create and reply)',
          },
          id: {
            type: 'string',
            description:
              'The comment id to act on (for reply, resolve and delete). For ' +
              'reply, either a root or one of its replies: both land in the ' +
              'same thread',
          },
          mentions: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                kind: { type: 'string', enum: ['session', 'document'] },
                id: { type: 'string' },
                label: { type: 'string' },
              },
              required: ['kind', 'id', 'label'],
            },
            description:
              'What this comment names (for create and reply). The body must ' +
              'contain "@label" exactly once for each entry. A session mention ' +
              'wakes that session; a document mention is a link',
          },
          author: {
            type: 'string',
            description: 'Optional author id (defaults to "agent")',
          },
        },
        required: ['action'],
      },
      execute: async (a) => {
        const action = String(a.action ?? '').trim()
        const rawTarget =
          a.target && typeof a.target === 'object'
            ? (a.target as Record<string, unknown>)
            : {}
        // Keep only the recognised, string-valued target fields. Every one of
        // them is a filter field for `list` too, which is what makes "every
        // comment on this file" and "every comment on this passage" askable.
        const target: Record<string, string> = {}
        for (const k of TARGET_FIELDS) {
          if (typeof rawTarget[k] === 'string' && rawTarget[k])
            target[k] = rawTarget[k] as string
        }
        const author = String(a.author ?? '').trim() || 'agent'
        if (action === 'create') {
          const body = String(a.body ?? '').trim()
          if (!body) throw new Error('body is required to create a comment')
          // A comment lives on the page or session it is about (RFC 0339 3a),
          // so a diff line needs the review document it is on as well.
          if (!target.documentId && !target.sessionId) {
            throw new Error(
              'a target with a documentId or a sessionId is required (a diff ' +
                'line also needs the documentId of the review it is on), ' +
                'or use action "reply" to answer an existing comment',
            )
          }
          const mentions = resolveToolMentions(a.mentions, body)
          const projectId = await registryFor(subjectQuery(target))
          const id = await createComment(repo, projectId, {
            target,
            author,
            body,
            ...(mentions.length > 0 ? { mentions } : {}),
            ...(sessionId ? { authorSessionId: sessionId } : {}),
          })
          if (mentions.length === 0) {
            // RFC 0301 §3d: a comment naming nobody goes to the session of the
            // SPACE it was made in, and a session has no space. Name one.
            return `Created comment ${id}`
          }
          const out = await deliverMentions(repo, projectId, {
            commentId: id,
            mentions,
            author,
            body,
            ...(target.documentId ? { documentId: target.documentId } : {}),
            ...(target.anchorText ? { anchorText: target.anchorText } : {}),
            ...(sessionId ? { authorSessionId: sessionId } : {}),
          })
          return `Created comment ${id}${describeDelivery(out)}`
        }
        if (action === 'reply') {
          const id = String(a.id ?? '').trim()
          if (!id) throw new Error('id is required to reply to a comment')
          const body = String(a.body ?? '').trim()
          if (!body) throw new Error('body is required to reply to a comment')
          const projectId = await registryFor({ commentId: id })
          // Through answerComment, not replyToComment, so an agent answering
          // another agent's comment wakes it exactly as a person's reply does.
          // One path, or the rule holds only for whoever remembered it.
          const mentions = resolveToolMentions(a.mentions, body)
          const out = await answerComment(repo, projectId, {
            parentId: id,
            author,
            body,
            ...(mentions.length > 0 ? { mentions } : {}),
            ...(sessionId ? { authorSessionId: sessionId } : {}),
          })
          const told = out.notifiedSessionId
            ? `Replied to ${id} with comment ${out.id} (told session ${out.notifiedSessionId})`
            : `Replied to ${id} with comment ${out.id}`
          if (mentions.length === 0) return told
          // The reply's own mentions are a SECOND edge: RFC 0289 wakes whoever
          // wrote the comment being answered, RFC 0292 wakes whoever the answer
          // names. A reply that hands a question on should reach both.
          const delivered = await deliverMentions(repo, projectId, {
            commentId: out.id,
            mentions,
            author,
            body,
            ...(sessionId ? { authorSessionId: sessionId } : {}),
          })
          return `${told}${describeDelivery(delivered)}`
        }
        if (action === 'resolve') {
          const id = String(a.id ?? '').trim()
          if (!id) throw new Error('id is required to resolve a comment')
          await resolveComment(repo, await registryFor({ commentId: id }), id)
          return `Resolved comment ${id}`
        }
        if (action === 'delete') {
          const id = String(a.id ?? '').trim()
          if (!id) throw new Error('id is required to delete a comment')
          await deleteComment(repo, await registryFor({ commentId: id }), id)
          return `Deleted comment ${id}`
        }
        if (action === 'list') {
          const projectId = await registryFor(subjectQuery(target))
          const handle = await repo.find(projectId as AnyDocumentId)
          await ready(handle as { whenReady?: () => Promise<void> })
          // Where the comments live (RFC 0339): the page's or session's
          // comments document, or every one the project indexes.
          const threads = projectThreads(
            await loadComments(repo, projectId, target),
            target,
          )
          if (threads.length === 0) return '(no comments)'
          // Threads, not a flat list: a reply printed as a peer of the thing it
          // answers is why an agent reading this could not tell an exchange
          // from two unrelated opinions. Replies are indented under their root.
          // What the comment is ON, printed only on a root. Both halves of the
          // anchor when there are two: the id is what `list` filters by and the
          // quoted label is the only part a reader understands, so printing one
          // without the other leaves the agent unable to act or unable to tell
          // what was meant.
          const anchorOf = (c: CommentEntry): string => {
            const text = c.target?.anchorText
            const node = c.target?.nodeId
            if (text && node) return `"${text}" (nodeId ${node})`
            if (text) return `"${text}"`
            if (node) return `nodeId ${node}`
            return ''
          }
          const line = (c: CommentEntry, indent: string) => {
            const anchor = anchorOf(c)
            return (
              `${indent}${c.id}  [${c.resolved ? 'resolved' : 'open'}]  ` +
              // The writer's name, not their id (RFC 0324 3a).
              `${projectAuthor(handle.doc(), c).label}: ${c.body}` +
              (indent === '' && anchor ? `\n    on: ${anchor}` : '')
            )
          }
          return threads
            .map((t) =>
              [line(t.root, ''), ...t.replies.map((r) => line(r, '  ↳ '))].join(
                '\n',
              ),
            )
            .join('\n')
        }
        throw new Error(
          `unknown action "${action}" (create | reply | resolve | delete | list)`,
        )
      },
    },
  }
}

/**
 * APP tools (RFC 0267): register an app the agent has built, and list what is
 * registered. The agent authors the view and skill as ordinary documents
 * (create_document), then calls install_package (kind: 'app') to tie them to a data type under a
 * stable id. No app is a monorepo fixture; this is how one comes into being.
 */
export function appTools(
  repo: Repo,
  projectId: string,
  /** The World, whose apps are this project's too (RFC 0338 S6). Absent on a
   *  World-less host, which reads the project catalog alone. */
  opts: { worldId?: string | null } = {},
): Record<string, ToolDef> {
  const readWorldDoc = async (): Promise<unknown> => {
    if (!opts.worldId) return undefined
    const h = await repo.find(opts.worldId as AnyDocumentId)
    await ready(h as { whenReady?: () => Promise<void> })
    return h.doc()
  }
  return {
    list_apps: {
      description:
        "List the apps this project has (RFC 0267), its own and the World's: name, the data " +
        'type each owns, and its view and skill document ids. Use this to find ' +
        'an app before changing it — guessing from document names misses apps ' +
        'and invents ones that were never registered.',
      inputSchema: { type: 'object', properties: {} },
      execute: async () => {
        const handle = await repo.find(projectId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        // Apps that own records, the project's and the World's. Chrome,
        // layouts and thread renderers are packages: list_packages lists them.
        const apps = installedApps(await readWorldDoc(), handle.doc()).filter(
          (a) =>
            a.dataType || (a.handles?.length && !a.handles.includes('agent')),
        )
        if (apps.length === 0) return '(no apps registered)'
        return apps
          .map(
            (a) =>
              `${a.id}  ${a.name}  dataType=${a.dataType}  ` +
              `view=${a.viewDocId}  skill=${a.skillDocId}` +
              (a.scope === 'world' ? '  (World)' : ''),
          )
          .join('\n')
      },
    },
    read_app: {
      description:
        "Read a registered app's FULL record: its schema (the fields an " +
        'instance carries), its capabilities, its runtime, and the ids of its ' +
        'view and skill documents. read_document does NOT work on an app — an ' +
        'app record has no text content. Read this before changing an app, so ' +
        'you are correcting what it actually declares rather than guessing.',
      inputSchema: {
        type: 'object',
        properties: {
          appId: { type: 'string', description: 'The app id from list_apps' },
        },
        required: ['appId'],
      },
      execute: async (a) => {
        const appId = String(a.appId ?? '')
        if (!appId) return 'appId required'
        const handle = await repo.find(appId as AnyDocumentId)
        await ready(handle as { whenReady?: () => Promise<void> })
        const app = readApp(handle.doc())
        if (!app) return `No app ${appId}`
        return JSON.stringify(
          {
            id: app.id,
            name: app.name,
            description: app.description,
            dataType: app.dataType,
            runtime: app.runtime,
            viewDocId: app.viewDocId,
            skillDocId: app.skillDocId,
            capabilities: app.capabilities,
            schema: app.schema,
            panels: app.panels ?? [],
            // RFC 0271. Reported explicitly rather than omitted when absent, so
            // "this app is an application" and "this build does not know about
            // presentation" cannot look the same — which is exactly the
            // confusion that made a flip impossible to confirm.
            presentation: app.presentation ?? 'application',
            // Reported for the same reason, and it is the one that decides
            // whether a record can be opened on its own. Absent would read as
            // "this build has no such idea" rather than "this app said no".
            instancesOpenable: app.instancesOpenable === true,
          },
          null,
          2,
        )
      },
    },
    update_app: {
      description:
        'Update a registered app IN PLACE, keeping its id. Use this when the ' +
        'view or skill has changed and the registration no longer describes ' +
        'it — an out-of-date schema is worse than none, because it feeds the ' +
        'agent prompt and makes agents write fields the view does not read. Do ' +
        'NOT install it again to fix a registration: that mints a ' +
        'SECOND app on the same dataType. dataType itself cannot be changed; ' +
        'it is the identity of every existing instance.',
      inputSchema: {
        type: 'object',
        properties: {
          appId: { type: 'string', description: 'The app id from list_apps' },
          name: { type: 'string' },
          description: { type: 'string' },
          schema: {
            type: 'object',
            properties: {},
            description: 'The complete new schema spec (fields + example)',
          },
          viewDocId: { type: 'string' },
          skillDocId: { type: 'string' },
          presentation: {
            type: 'string',
            description:
              'RFC 0271: "application" (a place you go, keeping its own index) ' +
              'or "document" (each instance is a document in the sidebar, opened ' +
              'on its own). Absent means "application". Flipping to "document" ' +
              'is what lets an app DELETE its index screen.',
          },
          instancesOpenable: {
            type: 'boolean',
            description:
              "List and open this app's records on their own, WITHOUT giving " +
              'up its index. Set this when a record is a thing somebody links ' +
              'to and puts in a pane (a canvas, a review) while the app still ' +
              'has a useful index of them. Leave it off for records that are ' +
              'only rows in that index (a calendar event, a CRM contact): ' +
              "listing those beside the project's documents buries the " +
              'documents. A "document" app is already openable and ignores it.',
          },
          panels: {
            type: 'array',
            items: {},
            description:
              'RFC 0273: sections this app adds to the canvas side column, ' +
              'e.g. [{"id":"sessions","title":"Sessions","kind":"view"}]. ' +
              'Pass [] to remove every section this app contributes.',
          },
          runtime: { type: 'string' },
          capabilities: {
            type: 'object',
            properties: {},
            description:
              'The complete new { writes, reads }. Changing `reads` installs ' +
              'or releases capabilities, so declare only what the app uses.',
          },
          icon: {
            type: 'object',
            properties: {},
            description:
              "RFC 0332: the app's tile. Any of { letter (1-2 chars), code " +
              '(2 chars), color (red, yellow, blue, green, black, cream, grey, ' +
              'pink), mark (a mark name such as "circles", "check", "slides", ' +
              '"calendar", or { path } on a 24 grid) }. Omitted fields are ' +
              'generated from the app. {} resets the icon to generated. For ' +
              'an app built from source, also write it into the mod block of ' +
              "the app's package.json, or the next push will not carry it.",
          },
        },
        required: ['appId'],
      },
      execute: async (a) => {
        const appId = String(a.appId ?? '')
        if (!appId) return 'appId required'
        const patch: Parameters<typeof updateApp>[3] = {}
        if (typeof a.name === 'string') patch.name = a.name
        if (typeof a.description === 'string') patch.description = a.description
        if (a.schema && typeof a.schema === 'object') {
          patch.schema = a.schema as AppSchemaSpec
        }
        if (typeof a.viewDocId === 'string') patch.viewDocId = a.viewDocId
        if (typeof a.skillDocId === 'string') patch.skillDocId = a.skillDocId
        // Array.isArray, NOT truthiness: updateApp treats an empty array as
        // "remove every section", so `panels: []` is the only way to stop
        // contributing without unregistering. A .length guard makes that
        // unreachable and the removal silently does nothing.
        if (Array.isArray(a.panels)) {
          patch.panels = readSlotFills(a.panels)
        }
        if (a.presentation === 'document' || a.presentation === 'application') {
          patch.presentation = a.presentation
        }
        if (typeof a.instancesOpenable === 'boolean') {
          patch.instancesOpenable = a.instancesOpenable
        }
        if (typeof a.runtime === 'string') patch.runtime = a.runtime
        if (a.capabilities && typeof a.capabilities === 'object') {
          patch.capabilities = a.capabilities as Partial<AppCapabilities>
        }
        if (a.icon && typeof a.icon === 'object') {
          // Strings off the log can arrive as Automerge ImmutableStrings, so
          // each field is made a plain string before it is checked.
          const raw = a.icon as Record<string, unknown>
          const str = (v: unknown) => (v == null ? undefined : String(v))
          const mark =
            raw.mark && typeof raw.mark === 'object'
              ? { path: String((raw.mark as { path?: unknown }).path ?? '') }
              : str(raw.mark)
          const icon = Object.fromEntries(
            Object.entries({
              letter: str(raw.letter),
              code: str(raw.code),
              color: str(raw.color),
              mark,
            }).filter(([, v]) => v !== undefined),
          )
          const iconError = iconSpecError(icon)
          if (iconError) return `Not updated: ${iconError}`
          // An empty icon is "back to generated", which is a delete.
          patch.icon = Object.keys(icon).length ? (icon as IconSpec) : null
        }
        if (Object.keys(patch).length === 0) return 'Nothing to update'
        // Into EVERY catalog that holds the entry. A World app's entry is in
        // the World, and an app installed into a second project from the
        // missing-app prompt has a copy there too (RFC 0338 S7): updating one
        // left the others drawing the old view while reporting success.
        const project = await repo.find(projectId as AnyDocumentId)
        await ready(project as { whenReady?: () => Promise<void> })
        const world = await readWorldDoc()
        const catalogs = [
          projectId,
          ...(opts.worldId ? [opts.worldId] : []),
          ...projectWorldProjects(world)
            .map((p) => p.projectId)
            .filter((id) => id !== projectId),
        ]
        const holding: string[] = []
        for (const id of catalogs) {
          const h = await repo.find(id as AnyDocumentId)
          await ready(h as { whenReady?: () => Promise<void> })
          if ((h.doc() as { apps?: Record<string, unknown> })?.apps?.[appId])
            holding.push(id)
        }
        if (holding.length === 0)
          return `Not updated: ${appId} is not an app this project has`
        for (const catalogId of holding) {
          await updateApp(repo, projectId, appId, { ...patch, catalogId })
        }
        return `Updated app ${appId} (${Object.keys(patch).join(', ')})`
      },
    },
    /**
     * NO register_app. It is `install_package` with `kind: 'app'` (RFC 0284
     * S6k).
     *
     * There were three install tools — this one, `install_chrome` and
     * `install_thread` — because each kind of package arrived in its own slice
     * and a separate tool was the smallest change each time. An agent then had
     * to know which verb a sidebar needed, and a sixth kind meant a sixth verb.
     * The kind is a field now, and this file keeps the app READS and updates.
     *
     * `instancesOpenable` (PR #264) arrived on this tool while it was being
     * deleted here. It is carried on `install_package`, and `update_app` and
     * `read_app` below keep their halves of it: a field that only half survives
     * a merge is worse than one that does not survive at all.
     */
    // No list_apps tool: the <available_apps> prompt block already lists every
    // app in the agent's context, so a tool would only duplicate it and add to
    // tool count (which degrades agents). Discovery is the prompt's job.
  }
}

/**
 * The `<available_apps>` system-prompt block (RFC 0267 &sect;5d, searugast's
 * `<available_skills>` applied to apps). Lists the project's registered apps
 * so a serving agent knows what exists and how to operate each: the data type
 * to tag instances with, the fields, and the skill doc to read for conventions.
 * Returns '' when there are no apps, so a project with none adds nothing to
 * the prompt. A host appends this to the project system prompt at serve time.
 */
export function formatAvailableApps(
  projectDoc: unknown,
  /** The World, whose apps this project has too (RFC 0338 S6). */
  worldDoc?: unknown,
): string {
  const apps = installedApps(worldDoc, projectDoc).filter((a) => a.dataType)
  if (apps.length === 0) return ''
  const lines = apps.map((app) => {
    // Schema lives in the app doc + the skill; the prompt points there rather
    // than inlining fields, so this stays a zero-hydration read of the catalog.
    return (
      `- ${app.name} (dataType "${app.dataType}"): ${app.description}\n` +
      `    skill: read_document ${app.skillDocId} for the field shape and conventions`
    )
  })
  return (
    '<available_apps>\n' +
    'Apps in this project. To operate one, create/list/update documents ' +
    'tagged with its dataType using the document tools, and read its skill for ' +
    'the field shape and conventions.\n' +
    `${lines.join('\n')}\n` +
    '</available_apps>'
  )
}

/** What a move to another project says about app records it could not open
 *  there (RFC 0338 3f): the move does not install the app, so it says so. */
function missingAppsNote(types: readonly string[]): string {
  if (types.length === 0) return ''
  return `. ${types.join(', ')} ${types.length === 1 ? 'is' : 'are'} not installed there, so ${types.length === 1 ? 'that record opens' : 'those records open'} to a prompt to install it; install_package { kind: 'app', scope: 'world', appId } installs it for every project`
}
